import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CompanyService,
  CurrencyService,
  getBudgetStageByValue,
  getSafeImageUrl,
  GlobalValuesService,
  OpportunityService,
  ScreenService
} from '@app/core';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import {
  CreateBudgetLineDrawerComponent
} from '../../drawers/create-budget-line-drawer/create-budget-line-drawer.component';
import {
  CreateSaleOrderLineDrawerComponent
} from '../../drawers/create-sale-order-line-drawer/create-sale-order-line-drawer.component';
import { combineLatest, forkJoin, of, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import {
  BudgetLineIncludeOption,
  ConfirmationModalComponent,
  isProductInCompanyScope,
  SaleOrderLineIncludeOption,
  ToastConfig,
} from '@app/shared';
import { DomSanitizer } from '@angular/platform-browser';
import { QuillEditorComponent } from 'ngx-quill';
import {
  convertCurrencyAmount,
  CurrencyConversionError,
  normalizePositiveBaseRate,
} from './currency-conversion.util';
import { getCompanyTaxesForProduct, getCompanyTaxesForMissingProduct } from './budget-content-tax-filter.util';
import { dedupeTaxOptions, restrictToQuoteCompany } from '../../tax-options.util';
import {
  ResolvedLineTax,
  formatLineTaxes,
  resolveLineTaxes,
  sumLineTaxAmount,
} from './budget-content-tax-display.util';
import { resolveSaleOrderName } from '../../quotation-name-display.util';
import { focusStaysInsideEditor } from './budget-content-terms-editor.util';
import {
  LineWriteErrorNotice,
  resolveLineWriteErrorMessage,
  shouldNotifyLineWriteError
} from './budget-content-line-error.util';

interface BudgetTableColumn {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'boolean';
  format?: 'number';
  fixed?: boolean;
  placeholder?: string;
}

interface BudgetLineRowViewModel {
  line: any;
  index: number;
  isAssociatedCost: boolean;
  parentSaleId: number | null;
  associatedCostCount: number;
  isExpanded: boolean;
}

interface BudgetLineSummary {
  description: string;
  typeLabel: 'Venta' | 'Coste';
  productName: string;
  associatedSaleName: string;
  associatedCostCount: number;
  totalSale: number;
  totalCost: number;
  grossMargin: number;
  marginPercent: number;
  currencyCode: string;
}

@Component({
  selector: 'app-budget-content',
  standalone: false,
  templateUrl: './budget-content.component.html',
  styleUrl: './budget-content.component.less'
})
export class BudgetContentComponent implements OnInit, OnChanges, AfterViewInit {
  private readonly inlineAutosaveFields = new Set([
    'name',
    'isActive',
    'partnerId',
    'driveUrl',
    'budgetTemplate',
    'expirationDate',
    'currencyId',
    'paymentConditions',
    'terms',
  ]);
  private termsAutosaveTimeout?: ReturnType<typeof setTimeout>;

  private readonly budgetLineIncludes: BudgetLineIncludeOption[] = [
    'Product',
    'Supplier',
    'Currency',
    'UnitOfMeasure',
    'AssociatedSaleLine',
    'Source',
    'Destination',
    // CEC-663: 'TaxDetails', no 'Taxes'. La segunda trae la relación sin el impuesto —`tax: null`—
    // y la rejilla necesita el nombre y el porcentaje para pintar la columna y sumar el Total.
    'TaxDetails',
  ];

  private readonly saleOrderLineIncludes: SaleOrderLineIncludeOption[] = [
    'Product',
    'UnitOfMeasure',
    'Currency',
    'BudgetLine',
    'Source',
    'Destination',
    // CEC-663: 'TaxDetails', no 'Taxes'. Ver la nota de budgetLineIncludes.
    'TaxDetails',
    'BudgetLineProduct',
  ];

  @Input() budget: any = null;
  @Input() opportunity: any = null;
  @Input() budgetList: any[] = [];
  @Input() currentBudgetIndex: number = 0;
  @Input() activeTabIndex: number = 0;
  /** Bumped by the parent drawer after a budget PDF version is generated, to reload the "Versiones" tab. */
  @Input() pdfVersionsRefreshKey: number = 0;
  @Input() editPermissions: boolean = false;
  @Input() isEditModeEnabled: boolean = false;
  @Input() editModeAction: 'none' | 'save' | 'cancel' = 'none';
  @Output() updateEvent = new EventEmitter<any>();
  @Output() fieldUpdate = new EventEmitter<{ path: string; value: any; previousValue?: any }>();
  @Output() budgetChange = new EventEmitter<{ isNext: boolean; isPrev: boolean; budgetId?: number }>();
  @Output() quotationLinesAvailabilityChange = new EventEmitter<boolean>();
  @Output() currencyConversionValidityChange = new EventEmitter<boolean>();

  // Estado
  afterViewInit: boolean = false;

  // Propiedades computadas
  budgetName: string = '-';
  saleOrderName: string = '-';
  currentBudgetId: number = 0;
  hasMultipleBudgets: boolean = false;
  customerCompanyName: string = '-';
  contactName: string = '-';
  budgetCurrencyCode: string = 'USD';
  showCurrencyChangeNotification: boolean = false;
  previousCurrencyId: number | null = null;

  currentStageName: string = 'Presupuesto';
  currentStageValue: string = 'Budget'; // Valor del backend

  // Datos del presupuesto
  budgetData: any = {};
  todayISOString: string = new Date().toISOString();

  // Estado de edición inline
  currentEditingField: string | null = null;
  backupFieldValues: { [key: string]: any } = {};
  tempFieldValues: { [key: string]: any } = {};
  private isProcessingClick: boolean = false;
  isInsertingImage: boolean = false; // Bandera para evitar emisiones durante inserción de imágenes
  isAddingLink: boolean = false; // Bandera para evitar que se cierre el modo edición al abrir el modal de link
  private lastEmittedContent: string | null = null; // Último contenido emitido para evitar duplicados
  /**
   * CEC-692. Lo que muestra el editor de términos mientras está abierto. Es su propia copia: el drawer
   * padre escribe en `budget.terms` cuando vuelve cada guardado automático, y si ese valor llegaba al
   * editor —ya viejo, porque el usuario había seguido escribiendo— lo repintaba, el cursor saltaba y se
   * perdían las últimas pulsaciones. Mientras el editor está abierto, manda lo que hay en el editor.
   */
  termsEditorContent = '';

  private modalService = inject(NzModalService);
  private viewContainerRef = inject(ViewContainerRef);
  private notificationService = inject(NzNotificationService);

  // CEC-687. Aviso de la casa para los fallos al guardar una línea, que antes solo iban a la consola.
  @ViewChild('notificationTmpl') notificationTmpl?: TemplateRef<any>;
  notRef?: NzNotificationRef;
  toastConfig: ToastConfig = {
    icon: 'dangerous',
    iconColor: '#CB2A2F',
    content: '',
    showUndo: false,
  };
  private lastLineWriteError: LineWriteErrorNotice = {};

  // Cache para date pickers
  cachedDatePickerValue: Date | null = null;
  private datePickerEditingStartedAt = 0;

  // Configuración de Quill para texto enriquecido
  quillConfig = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'], // toggled buttons
        ['blockquote', 'code-block'],
        [{ header: 1 }, { header: 2 }], // custom button values
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
        [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
        [{ direction: 'rtl' }], // text direction
        [{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }], // dropdown with defaults from theme
        [{ font: [] }],
        [{ align: [] }],
        ['clean'], // remove formatting button
        ['link'],
      ],
      handlers: {
        link: (value: boolean) => {
          const quillInstance = this.budgetQuillEditor?.quillEditor;
          if (!quillInstance) return;

          const range = quillInstance.getSelection(true);

          if (value) {
            // Agregar o editar link
            if (range && range.length > 0) {
              const formats = quillInstance.getFormat(range);
              const currentLink = formats['link'] as string | undefined;
              this.showLinkModal(currentLink || '', quillInstance, { index: range.index, length: range.length });
            } else {
              this.modalService.warning({
                nzTitle: 'Seleccione texto',
                nzContent: 'Por favor, seleccione el texto al que desea agregar el enlace.',
                nzCentered: true,
                nzWidth: 400
              });
            }
          } else {
            if (range && range.length > 0) quillInstance.format('link', false);
          }
        },
      },
    },
  };

  // Resumen
  discount: number = 0;
  baseAmount: number = 0;
  total: number = 0;
  margin: number = 0;
  taxesBreakdown: Array<{ taxId: number, taxName: string, amount: number }> = [];

  // Resumen de presupuesto
  totalSale: number = 0;
  totalCost: number = 0;
  grossMargin: number = 0;
  marginPercent: number = 0;
  budgetTaxesBreakdown: Array<{ taxId: number, taxName: string, amount: number }> = [];
  budgetBaseAmount: number = 0;
  budgetTotalWithTaxes: number = 0;
  selectedBudgetLineSummary: BudgetLineSummary | null = null;
  isBudgetLineSummaryModalVisible: boolean = false;

  // Líneas de presupuesto
  quotationLines: any[] = [];
  budgetLines: any[] = [];
  displayedBudgetLineRows: BudgetLineRowViewModel[] = [];
  private expandedBudgetLineKeys = new Set<string>();

  // Configuración de columnas de la tabla de líneas del pedido
  tableColumns: BudgetTableColumn[] = [
    { key: 'product', label: 'Producto', type: 'select', placeholder: 'Seleccione producto' },
    { key: 'name', label: 'Descripción', type: 'text', placeholder: 'Escribe...' },
    { key: 'supplier', label: 'Proveedor', type: 'select', placeholder: 'Seleccione proveedor' },
    { key: 'quantity', label: 'Cantidad', type: 'number' },
    { key: 'unit', label: 'Unidad', type: 'select', placeholder: 'Seleccione unidad' },
    { key: 'priceUnit', label: 'Precio unitario', type: 'number', format: 'number' },
    { key: 'taxes', label: 'Impuestos', type: 'select', placeholder: 'Seleccione impuesto' },
    { key: 'discount', label: 'Desc.%', type: 'number', format: 'number' },
    { key: 'sourceId', label: 'Origen', type: 'select', placeholder: 'Seleccione origen' },
    { key: 'destinationId', label: 'Destino', type: 'select', placeholder: 'Seleccione destino' },
    { key: 'amount', label: 'Importe', type: 'number', format: 'number', fixed: true }
  ];

  // Configuración de columnas de la tabla de presupuesto
  budgetTableColumns: BudgetTableColumn[] = [
    { key: 'product', label: 'Producto', type: 'text', placeholder: 'Escribe...' },
    { key: 'name', label: 'Descripción', type: 'text', placeholder: 'Escribe...' },
    { key: 'isCost', label: 'Tipo', type: 'boolean' },
    { key: 'associatedSale', label: 'Venta asociada', type: 'text', placeholder: 'Escribe...' },
    { key: 'isUnitPrice', label: 'Unitario', type: 'boolean' },
    { key: 'supplier', label: 'Proveedor', type: 'select', placeholder: 'Seleccione proveedor' },
    { key: 'quantity', label: 'Cantidad estimada', type: 'number' },
    { key: 'unitMeasure', label: 'Unidad', type: 'text', placeholder: 'Escribe...' },
    { key: 'price', label: 'Precio', type: 'number', format: 'number' },
    { key: 'taxes', label: 'Impuestos', type: 'select', placeholder: 'Seleccione impuesto' },
    { key: 'discount', label: 'Descuento', type: 'number', format: 'number' },
    { key: 'amount', label: 'Importe', type: 'number', format: 'number', fixed: true }
  ];

  // Estados de edición para celdas de la tabla (usando un objeto para simplificar)
  editingCells: { [key: string]: number | null } = {};
  editingLineNoteIndex: number | null = null;
  editingLineNoteContext: 'quotation' | 'budget' | null = null;
  lineNoteDraft: string = '';


  // Almacenar taxes filtrados por índice de línea para mejor rendimiento
  filteredTaxesForQuotationLines: { [key: number]: any[] } = {};
  filteredTaxesForBudgetLines: { [key: number]: any[] } = {};

  // ViewChildren para inputs de la tabla (usando un objeto para simplificar)
  @ViewChildren('tableInput') tableInputs!: QueryList<ElementRef>;
  @ViewChildren('lineNoteInput') lineNoteInputs!: QueryList<ElementRef<HTMLTextAreaElement>>;

  @ViewChild('deleteLineConfirmationModal') deleteLineConfirmationModal!: ConfirmationModalComponent;
  @ViewChild('budgetQuillEditor', { static: false }) budgetQuillEditor?: QuillEditorComponent;
  // CEC-692: el elemento que envuelve el editor, con su barra: para saber si el foco sigue dentro de él.
  @ViewChild('budgetQuillEditor', { read: ElementRef, static: false }) budgetQuillEditorHost?: ElementRef<HTMLElement>;
  @ViewChild('linkModalTemplate', { static: false }) linkModalTemplate!: TemplateRef<any>;
  @ViewChild('linkInput', { static: false }) linkInput?: ElementRef<HTMLInputElement>;
  linkModalUrl: string = '';
  linkModalHadLink: boolean = false; // true cuando se abre el modal para editar un enlace existente (mostrar "Quitar enlace")
  private linkModalQuill: any = null;
  private linkModalRange: { index: number; length: number } | null = null;
  private linkModalRef: any = null;
  private pendingDeleteIndex: number | null = null;
  private isDeletingBudgetLine: boolean = false;
  private pendingDeleteSaleOrderLineIndex: number | null = null;
  private isDeletingSaleOrderLine: boolean = false;

  // Listas de referencia
  partnerList: any[] = [];
  supplierList: any[] = [];
  productList: any[] = [];
  productListForSale: any[] = []; // Productos filtrados para venta (quotationLines)
  unitOfMeasureList: any[] = [];
  locationList: any[] = [];
  budgetTemplates: any[] = [];
  priceLists: any[] = [];
  paymentConditions: any[] = [];
  currencies: any[] = [];
  companyTaxes: any[] = [];

  /** ISO of the budget currency for the "Versiones" tab; null (no 'USD' default) when it cannot be resolved. */
  get versionsBudgetCurrencyCode(): string | null {
    const currencyId = this.budget?.currencyId;
    return (
      this.budget?.currency?.isoCode ||
      this.globalValuesService.currencies$.value?.find((c: any) => c.id === currencyId)?.isoCode ||
      null
    );
  }

  private destroyRef = inject(DestroyRef);
  protected readonly globalValuesService = inject(GlobalValuesService);
  private opportunityService = inject(OpportunityService);
  private companyService = inject(CompanyService);
  private currencyService = inject(CurrencyService);
  private sanitizer = inject(DomSanitizer);
  protected readonly getSafeImageUrl = getSafeImageUrl;

  // Propiedades para conversión de monedas
  baseCurrencyId: number = 1;
  conversionDate: string = '';
  currencyConversionError: string | null = null;
  private quotationLinesLoaded = false;
  private budgetLinesLoaded = false;
  private currencyConversionBlocked = false;
  private readonly draftConversionDate = new Date().toISOString();
  private pendingCurrencyFieldUpdate: { path: string; value: any; previousValue?: any } | null = null;
  private currencyConversionGeneration = 0;
  private currencyChangeGeneration = 0;
  private currencyChangePending = false;

  // Flag para evitar recargar líneas después de guardar
  private justSaved: boolean = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private drawerService: NzDrawerService,
    public screenService: ScreenService
  ) { }

  ngOnInit(): void {
    this.loadReferenceData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['budget']) {
      this.resetCurrencyConversionState();
      this.resetTermsEditorForBudgetChange(changes['budget'].previousValue, changes['budget'].currentValue);

      // NO resetear flags si acabamos de guardar (las líneas ya están actualizadas localmente)
      if (!this.justSaved) {
        // Reset flags cuando cambia el budget
        this.quotationLinesLoaded = false;
        this.budgetLinesLoaded = false;
        this.quotationLinesAvailabilityChange.emit(false);
      }

      if (changes['budget'].firstChange) {
        // Primera vez que se recibe el budget - inicializar previousCurrencyId
        if (this.budget?.currencyId) {
          this.previousCurrencyId = this.budget.currencyId;
        }
        // Primera vez que se recibe el budget
        this.updateComputedProperties();
        // Cargar líneas solo cuando cambia el budget
        if (this.budget?.id && !this.isEditModeEnabled) {
          this.loadBudgetLines();
          this.loadSaleOrderLines();
        }
      } else {
        // Budget cambió después de la primera carga
        this.updateComputedProperties();

        // NO recargar líneas si acabamos de guardar (ya están actualizadas localmente)
        if (this.justSaved) {
          // Las líneas ya están actualizadas por los callbacks de las peticiones HTTP
          // Solo marcar como cargadas y resetear el flag
          this.quotationLinesLoaded = true;
          this.budgetLinesLoaded = true;
          this.justSaved = false;
        } else if (this.budget?.id && !this.isEditModeEnabled) {
          // Cargar líneas solo cuando cambia el budget y no acabamos de guardar
          this.loadBudgetLines();
          this.loadSaleOrderLines();
        }
      }
    }
    if (changes['budgetList']) {
      this.hasMultipleBudgets = (this.budgetList?.length || 0) > 1;
    }
    // Si se cancela la edición, restaurar valores de backup
    if (changes['editModeAction'] && changes['editModeAction'].currentValue === 'cancel') {
      this.restoreBackupValues();
      this.updateComputedProperties();
      // Resetear el flag si se cancela
      this.justSaved = false;
    }
    // Si se guarda, enviar todos los cambios al backend
    if (changes['editModeAction'] && changes['editModeAction'].currentValue === 'save') {
      // Marcar que acabamos de guardar antes de llamar a saveAllBudgetLines
      // Esto evitará que se recarguen las líneas cuando el budget se actualice después
      this.justSaved = true;
      this.saveAllBudgetLines();
      this.backupFieldValues = {};
      this.tempFieldValues = {};
    }
  }

  /**
   * Iniciales a partir de un partner (nombre + apellido)
   */
  getPartnerInitials(partner: any): string {
    if (!partner) return '?';
    const name = partner.name || '';
    const surName = partner.surName || '';
    let initials = '';
    if (name.trim()) initials += name.trim().charAt(0).toUpperCase();
    if (surName.trim()) initials += surName.trim().charAt(0).toUpperCase();
    return initials || '?';
  }

  ngAfterViewInit(): void {
    this.afterViewInit = true;
  }

  loadReferenceData(): void {
    // Cargar partners y suppliers
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isReady: boolean) => {
        if (isReady) {
          this.partnerList = this.globalValuesService.partnerlist || [];
          // Suppliers son partners con partnerTypeId === 6 o 7 (providers)
          this.supplierList = this.partnerList.filter((p: any) => p.partnerTypeId === 6 || p.partnerTypeId === 7) || [];
          this.cdr.detectChanges();
        }
      });

    // También obtener el valor actual si ya está listo
    if (this.globalValuesService.isPartnerReady$.value) {
      this.partnerList = this.globalValuesService.partnerlist || [];
      // Suppliers son partners con partnerTypeId === 6 o 7 (providers)
      this.supplierList = this.partnerList.filter((p: any) => p.partnerTypeId === 6 || p.partnerTypeId === 7) || [];
    }

    // Cargar productos filtrados por la compañía actual
    combineLatest([
      this.globalValuesService.products$,
      this.companyService.selectedCompanyId$
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([products, companyId]) => {
        if (companyId) {
          // Filtrar productos por la compañía actualmente seleccionada. Un producto global
          // (companyId null) es válido para cualquier compañía, igual que en el servidor.
          this.productList = (products || []).filter((product: any) =>
            isProductInCompanyScope(product, companyId)
          );

          // Productos para venta (quotationLines y budgetLines con isCost=false)
          this.productListForSale = this.productList.filter((product: any) =>
            product.saleOk === true
          );
        } else {
          this.productList = [];
          this.productListForSale = [];
        }
        this.cdr.detectChanges();
      });

    // Cargar unidades de medida
    this.globalValuesService.unitOfMeasures$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((units) => {
        this.unitOfMeasureList = units || [];
        this.cdr.detectChanges();
      });

    // Cargar localizaciones
    this.globalValuesService.places$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locations: any[]) => {
        this.locationList = locations || [];
        this.cdr.detectChanges();
      });

    // Cargar monedas de la company
    this.globalValuesService.currencies$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((currencies: any[]) => {
        this.currencies = currencies || [];
        // Actualizar priceLists con las monedas para el campo "Lista de precios"
        this.priceLists = this.currencies.map(currency => ({
          id: currency.id,
          name: currency.isoCode || currency.name || currency.code || `Currency ${currency.id}`
        }));
        // Actualizar código de moneda del presupuesto si ya está cargado
        if (this.budget?.currencyId) {
          const currency = this.currencies.find(c => c.id === this.budget.currencyId);
          this.budgetCurrencyCode = currency?.isoCode || 'USD';
        }
        this.cdr.detectChanges();
      });

    // CEC-689: el catálogo entero, sin filtrar por la compañía del conmutador. Qué se ofrece en cada línea
    // lo decide offerTaxes(): cada impuesto una vez, prefiriendo el de la compañía de la cotización.
    this.globalValuesService.companyTaxes$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((taxes) => {
        this.companyTaxes = taxes || [];
        // Si el catálogo llega después que las líneas, sus desplegables se recalculan con él.
        (this.quotationLines || []).forEach((_, index) => this.updateFilteredTaxesForLine(index, false));
        (this.budgetLines || []).forEach((_, index) => this.updateFilteredTaxesForLine(index, true));
        this.cdr.detectChanges();
      });

    // Cargar otras listas de referencia si es necesario
    // budgetTemplates, paymentConditions
  }

  // Métodos para líneas de presupuesto
  addQuotationLine(): void {
    // Solo permitir agregar líneas si está en modo edición
    if (!this.isEditModeEnabled || !this.editPermissions) {
      return;
    }

    // Agregar una nueva línea vacía directamente en la tabla
    const newLine = {
      // Sin ID = línea nueva, se creará con POST cuando se guarde
      product: null,
      name: '',
      supplier: null,
      quantity: 0,
      unit: null,
      priceUnit: 0,
      taxes: [], // Array de taxIds
      discount: 0,
      amount: 0
    };

    const newIndex = this.quotationLines.length;
    this.quotationLines.push(newLine);
    // Inicializar taxes filtrados para la nueva línea
    this.updateFilteredTaxesForQuotationLine(newIndex);
    this.calculateSummary();
    // this.cdr.detectChanges();

    // Opcional: iniciar edición de la primera celda de la nueva línea
    // Esto mejora la UX al permitir empezar a escribir inmediatamente
    setTimeout(() => {
      const newIndex = this.quotationLines.length - 1;
      if (this.tableColumns.length > 0) {
        const firstEditableColumn = this.tableColumns.find(col =>
          col.key === 'product' || col.key === 'quantity' || col.key === 'priceUnit'
        );
        if (firstEditableColumn) {
          this.startEditingCell(firstEditableColumn.key, newIndex);
        }
      }
    }, 0);
  }

  removeQuotationLine(index: number): void {
    const line = this.quotationLines[index];
    if (line?.id && this.budget?.id) {
      // Eliminar desde el servidor usando SaleOrderLine
      this.opportunityService.deleteSaleOrderLine(this.budget.id, [line.id])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.quotationLines.splice(index, 1);
            this.emitQuotationLinesAvailability();
            this.calculateSummary();
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.handleLineWriteError(error, 'Error deleting sale order line');
          }
        });
    } else {
      // Si no tiene ID, solo eliminar localmente (línea nueva no guardada)
      this.quotationLines.splice(index, 1);
      this.emitQuotationLinesAvailability();
      this.calculateSummary();
    }
  }

  loadBudgetLines(): void {
    if (!this.budget?.id) {
      this.budgetLines = [];
      this.refreshDisplayedBudgetLineRows();
      this.budgetLinesLoaded = true;
      this.checkAndConvertCurrencies();
      return;
    }

    this.opportunityService.getAdvancedBudgetLines(this.budget.id, {
      includes: this.budgetLineIncludes,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const lines = response?.data?.rows || [];

          if (lines && lines.length > 0) {
            this.budgetLines = lines.map((line: any) => this.buildBudgetLineFromData(line));

            // Calcular el amount para cada línea al cargar (ya que no viene del backend)
            // e inicializar taxes filtrados
            this.budgetLines.forEach((line, index) => {
              this.calculateBudgetLineAmount(index);
              this.updateFilteredTaxesForLine(index, true);
            });

            this.expandSalesWithAssociatedCosts();
            this.refreshDisplayedBudgetLineRows();
            this.calculateSummary();
            this.cdr.detectChanges();
          } else {
            this.budgetLines = [];
            this.refreshDisplayedBudgetLineRows();
            this.calculateSummary();
            this.cdr.detectChanges();
          }

          this.budgetLinesLoaded = true;
          this.checkAndConvertCurrencies();
        },
        error: (error) => {
          console.error('Error loading budget lines:', error);
          this.budgetLines = [];
          this.refreshDisplayedBudgetLineRows();
          this.budgetLinesLoaded = true;
          this.checkAndConvertCurrencies();
          this.cdr.detectChanges();
        }
      });
  }

  loadBudgetLineTaxesForBudgetLines(budgetLineIds: number[]): void {
    if (!Array.isArray(budgetLineIds) || budgetLineIds.length === 0) {
      this.budgetLines.forEach(line => {
        line.taxes = line.taxes || [];
      });
      return;
    }

    // Filtrar IDs válidos y eliminar duplicados
    const validIds = [...new Set(budgetLineIds.filter(id => id && id > 0))];

    if (validIds.length === 0) {
      this.budgetLines.forEach(line => {
        line.taxes = line.taxes || [];
      });
      return;
    }

    // Hacer múltiples llamadas individuales con forkJoin usando BudgetLineTaxRel
    const taxRequests = validIds.map(id =>
      this.opportunityService.getBudgetLineTaxes(id)
    );

    forkJoin(taxRequests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (responses: any[]) => {
          // Agrupar taxes por budgetLineId
          const taxesByLineId: { [key: number]: number[] } = {};

          responses.forEach((response, index) => {
            const lineId = validIds[index];
            const taxRels = response?.data?.rows || (Array.isArray(response?.data) ? response.data : []);

            taxRels.forEach((rel: any) => {
              const taxId = rel.taxId || (rel.tax?.id) || null;
              if (taxId && taxId > 0) {
                if (!taxesByLineId[lineId]) {
                  taxesByLineId[lineId] = [];
                }
                taxesByLineId[lineId].push(taxId);
              }
            });
          });

          // Asignar taxes a cada línea de budgetLines (taxIds directamente)
          this.budgetLines.forEach((line, index) => {
            if (line.id && taxesByLineId[line.id]) {
              line.taxes = taxesByLineId[line.id];
            } else {
              line.taxes = [];
            }
            // CEC-689: el desplegable se calcula sabiendo qué impuesto tiene ya la línea.
            this.updateFilteredTaxesForLine(index, true);
          });

          this.calculateSummary();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading budget line taxes:', error);
          this.budgetLines.forEach(line => {
            line.taxes = line.taxes || [];
          });
          this.calculateSummary();
          this.cdr.detectChanges();
        }
      });
  }

  loadSaleOrderLines(): void {
    if (!this.budget?.id) {
      this.quotationLines = [];
      this.quotationLinesLoaded = true;
      this.emitQuotationLinesAvailability();
      this.checkAndConvertCurrencies();
      return;
    }

    this.opportunityService.getAdvancedSaleOrderLines(this.budget.id, {
      includes: this.saleOrderLineIncludes,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          const lines = response?.data?.rows || [];

          if (lines && lines.length > 0) {
            this.quotationLines = lines.map((line: any) => this.buildQuotationLineFromData(line));

            // Calcular el amount para cada línea al cargar (ya que no viene del backend)
            // e inicializar taxes filtrados
            this.quotationLines.forEach((line, index) => {
              this.calculateQuotationLineAmount(index);
              this.updateFilteredTaxesForLine(index, false);
            });

            this.calculateSummary();
            this.cdr.detectChanges();
          } else {
            this.quotationLines = [];
            this.calculateSummary();
            this.cdr.detectChanges();
          }

          this.emitQuotationLinesAvailability();
          this.quotationLinesLoaded = true;
          this.checkAndConvertCurrencies();
        },
        error: (error) => {
          console.error('Error loading sale order lines:', error);
          this.quotationLines = [];
          this.emitQuotationLinesAvailability();
          this.quotationLinesLoaded = true;
          this.checkAndConvertCurrencies();
          this.cdr.detectChanges();
        }
      });
  }

  loadSaleOrderLineTaxesForQuotationLines(saleOrderLineIds: number[]): void {
    if (!Array.isArray(saleOrderLineIds) || saleOrderLineIds.length === 0) {
      this.quotationLines.forEach(line => {
        line.taxes = line.taxes || [];
      });
      return;
    }

    // Filtrar IDs válidos y eliminar duplicados
    const validIds = [...new Set(saleOrderLineIds.filter(id => id && id > 0))];

    if (validIds.length === 0) {
      this.quotationLines.forEach(line => {
        line.taxes = line.taxes || [];
      });
      return;
    }

    // Hacer múltiples llamadas individuales con forkJoin usando SaleOrderLineTaxRel
    const taxRequests = validIds.map(id =>
      this.opportunityService.getSaleOrderLineTaxes(id)
    );

    forkJoin(taxRequests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (responses: any[]) => {
          // Agrupar taxes por saleOrderLineId
          const taxesByLineId: { [key: number]: number[] } = {};

          responses.forEach((response, index) => {
            const lineId = validIds[index];
            const taxRels = response?.data?.rows || (Array.isArray(response?.data) ? response.data : []);

            taxRels.forEach((rel: any) => {
              // Extraer saleOrderLineId de la relación si está disponible, sino usar el lineId del índice
              const relLineId = rel.saleOrderLineId || lineId;
              const taxId = rel.taxId || (rel.tax?.id) || null;
              if (taxId && taxId > 0) {
                if (!taxesByLineId[relLineId]) {
                  taxesByLineId[relLineId] = [];
                }
                taxesByLineId[relLineId].push(taxId);
              }
            });
          });

          // Asignar taxes a cada línea de quotationLines (taxIds directamente)
          this.quotationLines.forEach((line, index) => {
            if (line.id && taxesByLineId[line.id]) {
              line.taxes = taxesByLineId[line.id];
            } else {
              line.taxes = [];
            }
            // CEC-689: el desplegable se calcula sabiendo qué impuesto tiene ya la línea.
            this.updateFilteredTaxesForLine(index, false);
          });

          this.calculateSummary();
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading sale order line taxes:', error);
          this.quotationLines.forEach(line => {
            line.taxes = line.taxes || [];
          });
          this.calculateSummary();
          this.cdr.detectChanges();
        }
      });
  }

  // Métodos para líneas de presupuesto
  addBudgetLine(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const createBudgetLineDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: CreateBudgetLineDrawerComponent,
          nzData: {
            budgetId: this.budget?.id || this.budget?.budgetId || '',
            budget: this.budget,
            budgetLines: this.budgetLines
          },
        });

        // Obtener referencia al componente del drawer después de que se abra para escuchar eventos
        createBudgetLineDrawer.afterOpen.subscribe(() => {
          const drawerComponent = createBudgetLineDrawer.getContentComponent() as CreateBudgetLineDrawerComponent;

          // Suscribirse al evento lineCreated para cuando se guarda y se crea nueva
          if (drawerComponent && drawerComponent.lineCreated) {
            drawerComponent.lineCreated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((lineData: any) => {
              this.handleBudgetLineDrawerResult(lineData);
            });
          }
        });

        // Agregar la línea cuando el drawer se cierre con datos
        createBudgetLineDrawer.afterClose.subscribe((result: any) => {
          if (result) {
            // Si result tiene una propiedad 'data', usarla; si no, usar result directamente
            // El drawer cierra con lineData directamente (response?.data || null)
            const lineData = result.data || result;
            if (Array.isArray(lineData?.budgetLines)) {
              this.handleBudgetLineDrawerResult(lineData);
              return;
            }
            // Verificar que lineData tenga al menos un id para confirmar que es una línea válida
            // O si no tiene id pero tiene name (es una línea nueva local)
            if (lineData && (lineData.id || lineData.data?.id || lineData.name)) {
              // Si lineData tiene una propiedad data anidada, usar esa
              const finalLineData = lineData.data || lineData;
              this.handleBudgetLineDrawerResult(finalLineData);
            }
          }
        });
      });
  }

  private handleBudgetLineDrawerResult(result: any): void {
    if (!result) {
      return;
    }

    if (Array.isArray(result.budgetLines)) {
      result.budgetLines.forEach((lineData: any) => this.addBudgetLineToTable(lineData));
      return;
    }

    this.addBudgetLineToTable(result);
  }

  private addBudgetLineToTable(lineData: any): void {
    if (!lineData) {
      return;
    }

    // Agregar la línea creada a budgetLines (líneas de presupuesto)
    const newLine = this.buildBudgetLineFromData(lineData);

    // Inicializar taxes filtrados para la nueva línea
    const lineIndex = this.budgetLines.length;
    this.budgetLines.push(newLine);
    this.expandParentSaleForAssociatedCost(newLine);
    this.refreshDisplayedBudgetLineRows();
    this.updateFilteredTaxesForLine(lineIndex, true);
    // Calcular el amount para la nueva línea (ya que puede no venir del backend o necesitar recálculo)
    this.calculateBudgetLineAmount(lineIndex);
    this.calculateSummary();
    this.cdr.detectChanges();
  }

  openEditBudgetLineDrawer(line: any, index: number): void {
    if (!this.editPermissions) {
      return;
    }

    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const editBudgetLineDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: CreateBudgetLineDrawerComponent,
          nzData: {
            budgetId: this.budget?.id || this.budget?.budgetId || '',
            budget: this.budget,
            lineData: line,
            budgetLines: this.budgetLines
          },
        });

        editBudgetLineDrawer.afterClose.subscribe((result: any) => {
          if (result) {
            const updatedLineData = result.data || result;
            if (Array.isArray(updatedLineData?.budgetLines)) {
              this.handleEditBudgetLineDrawerResult(updatedLineData, index);
              return;
            }

            if (updatedLineData && (updatedLineData.id || updatedLineData.data?.id)) {
              const finalLineData = updatedLineData.data || updatedLineData;
              this.updateBudgetLineAtIndex(index, finalLineData);
            }
          }
        });
      });
  }

  private handleEditBudgetLineDrawerResult(result: any, editedIndex: number): void {
    const editedLineId = this.extractEntityId(
      this.budgetLines[editedIndex]?.id,
      result?.lineData?.id
    );
    const budgetLines = result.budgetLines.map((lineData: any) => lineData?.data || lineData);
    const matchedEditedLineIndex = budgetLines.findIndex((lineData: any) => {
      const lineId = this.extractEntityId(
        lineData?.id,
        lineData?.budgetLineId,
        lineData?.budgetLine,
        lineData?.originalId
      );

      return !!editedLineId && lineId === editedLineId;
    });
    const editedLineResultIndex = matchedEditedLineIndex >= 0 ? matchedEditedLineIndex : 0;

    budgetLines.forEach((lineData: any, lineIndex: number) => {
      if (lineIndex === editedLineResultIndex) {
        this.updateBudgetLineAtIndex(editedIndex, lineData);
        return;
      }

      this.addBudgetLineToTable(lineData);
    });
  }

  private updateBudgetLineAtIndex(index: number, lineData: any): void {
    if (!lineData || !this.budgetLines[index]) {
      return;
    }

    const finalLineData = lineData.data || lineData;
    const previousNote = this.getLineNote(this.budgetLines[index]);
    this.budgetLines[index] = this.buildBudgetLineFromData(finalLineData);
    this.restoreBudgetLineNote(index, previousNote);
    this.expandParentSaleForAssociatedCost(this.budgetLines[index]);
    this.refreshDisplayedBudgetLineRows();

    if (finalLineData.taxIds) {
      this.budgetLines[index].taxes = finalLineData.taxIds;
    }

    this.calculateBudgetLineAmount(index);
    this.updateFilteredTaxesForLine(index, true);
    this.calculateSummary();
    this.cdr.detectChanges();
  }

  private refreshDisplayedBudgetLineRows(): void {
    const saleLineIds = new Set<number>();

    this.budgetLines.forEach(line => {
      if (line?.isCost === true) {
        return;
      }

      const saleLineId = this.getBudgetLineId(line);
      if (saleLineId) {
        saleLineIds.add(saleLineId);
      }
    });

    const associatedCostsBySaleId = new Map<number, Array<{ line: any; index: number }>>();

    this.budgetLines.forEach((line, index) => {
      const associatedSaleId = this.getAssociatedSaleLineId(line);
      const shouldNestAsCost = line?.isCost === true && associatedSaleId !== null && saleLineIds.has(associatedSaleId);

      if (!shouldNestAsCost) {
        return;
      }

      const costs = associatedCostsBySaleId.get(associatedSaleId) || [];
      costs.push({ line, index });
      associatedCostsBySaleId.set(associatedSaleId, costs);
    });

    const rows: BudgetLineRowViewModel[] = [];

    this.budgetLines.forEach((line, index) => {
      const associatedSaleId = this.getAssociatedSaleLineId(line);
      const shouldNestAsCost = line?.isCost === true && associatedSaleId !== null && saleLineIds.has(associatedSaleId);

      if (shouldNestAsCost) {
        return;
      }

      const saleLineId = line?.isCost === true ? null : this.getBudgetLineId(line);
      const associatedCosts = saleLineId ? associatedCostsBySaleId.get(saleLineId) || [] : [];
      const isExpanded = saleLineId
        ? this.expandedBudgetLineKeys.has(this.getBudgetLineExpandKey(line, index))
        : false;

      rows.push({
        line,
        index,
        isAssociatedCost: false,
        parentSaleId: null,
        associatedCostCount: associatedCosts.length,
        isExpanded
      });

      if (saleLineId && associatedCosts.length > 0 && isExpanded) {
        associatedCosts.forEach(cost => {
          rows.push({
            line: cost.line,
            index: cost.index,
            isAssociatedCost: true,
            parentSaleId: saleLineId,
            associatedCostCount: 0,
            isExpanded: false
          });
        });
      }
    });

    this.displayedBudgetLineRows = rows;
  }

  trackBudgetLineRow = (_index: number, row: BudgetLineRowViewModel): string => {
    const lineKey = this.getBudgetLineExpandKey(row.line, row.index);
    return row.isAssociatedCost
      ? `associated-cost:${row.parentSaleId || 'orphan'}:${lineKey}`
      : `budget-line:${lineKey}`;
  };

  toggleAssociatedCosts(row: BudgetLineRowViewModel, event?: Event): void {
    event?.stopPropagation();

    if (!row.associatedCostCount) {
      return;
    }

    const key = this.getBudgetLineExpandKey(row.line, row.index);

    if (this.expandedBudgetLineKeys.has(key)) {
      this.expandedBudgetLineKeys.delete(key);
    } else {
      this.expandedBudgetLineKeys.add(key);
    }

    this.refreshDisplayedBudgetLineRows();
  }

  openBudgetLineSummaryModal(row: BudgetLineRowViewModel, event?: Event): void {
    event?.stopPropagation();

    this.selectedBudgetLineSummary = this.buildBudgetLineSummary(row);
    this.isBudgetLineSummaryModalVisible = true;
  }

  closeBudgetLineSummaryModal(): void {
    this.isBudgetLineSummaryModalVisible = false;
    this.selectedBudgetLineSummary = null;
  }

  private buildBudgetLineSummary(row: BudgetLineRowViewModel): BudgetLineSummary {
    const line = row.line;
    const lineAmount = this.getBudgetLineSummaryAmount(line);
    const isCost = line?.isCost === true;
    const associatedSaleId = this.getAssociatedSaleLineId(line);
    const associatedSaleLine = associatedSaleId
      ? this.budgetLines.find(budgetLine => this.getBudgetLineId(budgetLine) === associatedSaleId)
      : null;

    const saleLine = isCost ? associatedSaleLine : line;
    const saleLineId = saleLine ? this.getBudgetLineId(saleLine) : null;
    const associatedCostLines = saleLineId ? this.getAssociatedCostLinesForSale(saleLineId) : [];

    const totalSale = isCost
      ? (associatedSaleLine ? this.getBudgetLineSummaryAmount(associatedSaleLine) : 0)
      : lineAmount;
    const totalCost = isCost
      ? lineAmount
      : associatedCostLines.reduce((sum, costLine) => sum + this.getBudgetLineSummaryAmount(costLine), 0);
    const grossMargin = totalSale - totalCost;

    return {
      description: line?.name || '-',
      typeLabel: isCost ? 'Coste' : 'Venta',
      productName: this.getProductName(line?.product, line?.productName),
      associatedSaleName: isCost ? this.getAssociatedSaleName(associatedSaleId) : '-',
      associatedCostCount: isCost ? 0 : associatedCostLines.length,
      totalSale,
      totalCost,
      grossMargin,
      marginPercent: totalSale > 0 ? grossMargin / totalSale : 0,
      currencyCode: this.budgetCurrencyCode
    };
  }

  private getAssociatedCostLinesForSale(saleLineId: number): any[] {
    return this.budgetLines.filter(line =>
      line?.isCost === true && this.getAssociatedSaleLineId(line) === saleLineId
    );
  }

  private getBudgetLineSummaryAmount(line: any): number {
    if (!line) {
      return 0;
    }

    return line.convertedAmount !== null && line.convertedAmount !== undefined
      ? line.convertedAmount
      : (line.amount || 0);
  }

  private expandParentSaleForAssociatedCost(line: any): void {
    if (line?.isCost !== true) {
      return;
    }

    const associatedSaleId = this.getAssociatedSaleLineId(line);
    if (associatedSaleId) {
      this.expandedBudgetLineKeys.add(`id:${associatedSaleId}`);
    }
  }

  private expandSalesWithAssociatedCosts(): void {
    this.budgetLines.forEach(line => this.expandParentSaleForAssociatedCost(line));
  }

  private getBudgetLineId(line: any): number | null {
    return this.extractEntityId(
      line?.id,
      line?.budgetLineId,
      line?.budgetLine,
      line?.originalId
    );
  }

  private getAssociatedSaleLineId(line: any): number | null {
    return this.extractEntityId(
      line?.associatedSale,
      line?.associatedSaleLineId,
      line?.associatedSaleLine
    );
  }

  private getBudgetLineExpandKey(line: any, index?: number): string {
    const lineId = this.getBudgetLineId(line);

    if (lineId) {
      return `id:${lineId}`;
    }

    return `index:${index ?? this.budgetLines.indexOf(line)}`;
  }

  confirmDeleteBudgetLine(index: number): void {
    if (!this.editPermissions) {
      return;
    }

    const line = this.budgetLines[index];
    if (!line) {
      return;
    }

    // Si es una línea de coste, verificar si tiene una línea de venta asociada
    // (esto es solo informativo, no bloquea la eliminación)
    // Pero si es una línea de venta (isCost=false), verificar si hay líneas de coste asociadas a ella
    if (line.isCost === false) {
      // Esta es una línea de venta en la tabla de presupuesto
      // Verificar si hay líneas de coste asociadas a esta línea de venta
      const saleLineId = line.id;
      if (saleLineId) {
        const associatedCostLines = this.budgetLines.filter(budgetLine => {
          if (budgetLine.isCost !== true || budgetLine.id === line.id) {
            return false;
          }

          // Normalizar associatedSale: puede ser número, objeto con id, o null
          let associatedSaleId = null;
          if (budgetLine.associatedSale !== null && budgetLine.associatedSale !== undefined) {
            if (typeof budgetLine.associatedSale === 'object' && budgetLine.associatedSale.id) {
              associatedSaleId = budgetLine.associatedSale.id;
            } else if (typeof budgetLine.associatedSale === 'number') {
              associatedSaleId = budgetLine.associatedSale;
            } else if (typeof budgetLine.associatedSale === 'string') {
              const parsed = parseInt(budgetLine.associatedSale);
              if (!isNaN(parsed)) {
                associatedSaleId = parsed;
              }
            }
          }

          // Comparar IDs normalizados
          const normalizedSaleLineId = typeof saleLineId === 'number' ? saleLineId : parseInt(String(saleLineId));
          const normalizedAssociatedSaleId = typeof associatedSaleId === 'number' ? associatedSaleId : (associatedSaleId ? parseInt(String(associatedSaleId)) : null);

          return normalizedAssociatedSaleId !== null && normalizedAssociatedSaleId === normalizedSaleLineId;
        });

        if (associatedCostLines.length > 0) {
          // Mostrar modal de advertencia
          const count = associatedCostLines.length;
          const lineText = count === 1 ? 'línea' : 'líneas';
          const asociadaText = count === 1 ? 'asociada' : 'asociadas';

          this.pendingDeleteIndex = null;
          this.isDeletingBudgetLine = false;
          this.deleteLineConfirmationModal.config = {
            ...this.deleteLineConfirmationModal.config,
            title: 'No se puede eliminar la línea',
            message: `No se puede eliminar esta línea de venta porque tiene ${count} ${lineText} de coste ${asociadaText}. Por favor, primero cambie o elimine la relación en las líneas de coste antes de eliminar esta línea de venta.`,
            confirmText: 'Entendido',
            cancelText: 'Cerrar',
            confirmButtonDanger: false,
            confirmButtonType: 'primary'
          };
          this.deleteLineConfirmationModal.open();
          return;
        }
      }
    }

    // Si no hay líneas asociadas o es una línea de coste, proceder con la eliminación normal
    this.pendingDeleteIndex = index;
    this.isDeletingBudgetLine = true;
    this.deleteLineConfirmationModal.config = {
      ...this.deleteLineConfirmationModal.config,
      title: 'Eliminar línea',
      message: '¿Está seguro de que desea eliminar esta línea de presupuesto? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonDanger: true,
      confirmButtonType: 'default'
    };
    this.deleteLineConfirmationModal.open();
  }

  // Métodos para líneas de pedido (sale order lines)
  addSaleOrderLine(): void {
    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const createSaleOrderLineDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: CreateSaleOrderLineDrawerComponent,
          nzData: {
            saleOrderId: this.budget?.id || this.budget?.budgetId || '',
            saleOrder: this.budget,
            saleOrderLines: this.quotationLines
          },
        });

        // Obtener referencia al componente del drawer después de que se abra para escuchar eventos
        createSaleOrderLineDrawer.afterOpen.subscribe(() => {
          const drawerComponent = createSaleOrderLineDrawer.getContentComponent() as CreateSaleOrderLineDrawerComponent;

          // Suscribirse al evento lineCreated para cuando se guarda y se crea nueva
          if (drawerComponent && drawerComponent.lineCreated) {
            drawerComponent.lineCreated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((lineData: any) => {
              this.handleSaleOrderLineDrawerResult(lineData);
            });
          }
        });

        // Agregar la línea cuando el drawer se cierre con datos
        createSaleOrderLineDrawer.afterClose.subscribe((result: any) => {
          if (result) {
            const lineData = result.data || result;
            this.handleSaleOrderLineDrawerResult(result);
            return;
            // Verificar que sea una línea válida (con id o con productId/name si es local)
            if (lineData && (lineData.id || lineData.data?.id || lineData.productId || lineData.name)) {
              const finalLineData = lineData.data || lineData;
              this.addSaleOrderLineToTable(finalLineData);
            }
          }
        });
      });
  }

  private handleSaleOrderLineDrawerResult(result: any): void {
    if (!result) {
      return;
    }

    const lineData = result.data || result;

    if (lineData.saleOrderLine || lineData.budgetLine) {
      if (lineData.saleOrderLine) {
        this.addSaleOrderLineToTable(lineData.saleOrderLine);
      }

      if (lineData.budgetLine) {
        this.addBudgetLineToTable(lineData.budgetLine);
      }

      return;
    }

    if (lineData && (lineData.id || lineData.data?.id || lineData.productId || lineData.name)) {
      const finalLineData = lineData.data || lineData;
      this.addSaleOrderLineToTable(finalLineData);
    }
  }

  private addSaleOrderLineToTable(lineData: any): void {
    if (!lineData) {
      return;
    }

    // Agregar la línea creada a quotationLines
    const newLine = this.buildQuotationLineFromData(lineData);

    // Inicializar taxes filtrados para la nueva línea
    const lineIndex = this.quotationLines.length;
    this.quotationLines.push(newLine);
    this.updateFilteredTaxesForLine(lineIndex, false);
    // Calcular el amount para la nueva línea (ya que puede no venir del backend o necesitar recálculo)
    this.calculateQuotationLineAmount(lineIndex);
    this.emitQuotationLinesAvailability();
    this.calculateSummary();
    this.cdr.detectChanges();
    this.refreshBudgetLinesAfterSaleOrderLineCreate(lineData);
  }

  private refreshBudgetLinesAfterSaleOrderLineCreate(lineData: any): void {
    if (!this.budget?.id || !lineData?.id) {
      return;
    }

    this.budgetLinesLoaded = false;
    this.loadBudgetLines();
  }

  private emitQuotationLinesAvailability(): void {
    this.quotationLinesAvailabilityChange.emit(this.quotationLines.length > 0);
  }

  openEditSaleOrderLineDrawer(line: any, index: number): void {
    if (!this.editPermissions) {
      return;
    }

    combineLatest({
      isMobile: this.screenService.isMobile$,
      isMedium: this.screenService.isMedium$,
    })
      .pipe(take(1))
      .subscribe(({ isMobile, isMedium }) => {
        const editSaleOrderLineDrawer = this.drawerService.create({
          nzWidth: 793,
          nzHeight: '98%',
          nzPlacement: isMobile ? 'bottom' : 'right',
          nzClosable: false,
          nzMaskClosable: false,
          nzMask: true,
          nzContent: CreateSaleOrderLineDrawerComponent,
          nzData: {
            saleOrderId: this.budget?.id || this.budget?.budgetId || '',
            saleOrder: this.budget,
            lineData: line,
            saleOrderLines: this.quotationLines
          },
        });

        editSaleOrderLineDrawer.afterClose.subscribe((result: any) => {
          if (result) {
            const updatedLineData = result.data || result;
            if (updatedLineData && (updatedLineData.id || updatedLineData.data?.id)) {
              const finalLineData = updatedLineData.data || updatedLineData;

              // Construir la línea actualizada desde los datos del servidor
              // buildQuotationLineFromData ya extrae los taxes correctamente usando extractTaxIds
              this.quotationLines[index] = this.buildQuotationLineFromData(finalLineData);

              this.calculateQuotationLineAmount(index);
              this.updateFilteredTaxesForLine(index, false);
              this.calculateSummary();
              this.cdr.detectChanges();
            }
          }
        });
      });
  }

  confirmDeleteSaleOrderLine(index: number): void {
    if (!this.editPermissions) {
      return;
    }

    this.pendingDeleteSaleOrderLineIndex = index;
    this.isDeletingSaleOrderLine = true;
    this.deleteLineConfirmationModal.config = {
      ...this.deleteLineConfirmationModal.config,
      title: 'Eliminar línea',
      message: '¿Está seguro de que desea eliminar esta línea de pedido? Esta acción no se puede deshacer.',
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      confirmButtonDanger: true,
      confirmButtonType: 'default'
    };
    this.deleteLineConfirmationModal.open();
  }

  onDeleteLineConfirmed($event?: any): void {
    if (this.isDeletingBudgetLine && this.pendingDeleteIndex !== null) {
      this.removeBudgetLine(this.pendingDeleteIndex);
      this.pendingDeleteIndex = null;
      this.isDeletingBudgetLine = false;
    } else if (this.isDeletingSaleOrderLine && this.pendingDeleteSaleOrderLineIndex !== null) {
      this.removeQuotationLine(this.pendingDeleteSaleOrderLineIndex);
      this.pendingDeleteSaleOrderLineIndex = null;
      this.isDeletingSaleOrderLine = false;
    }
  }

  getUnitName(unitId: number | null): string {
    if (!unitId) return '';
    const unit = this.globalValuesService.unitOfMeasures$.value.find(u => u.id === unitId);
    return unit ? unit.name : '';
  }

  getUnitsForLineProduct(productValue: any): any[] {
    const productId = this.extractEntityId(productValue);
    if (!productId) {
      return this.unitOfMeasureList;
    }

    const product = this.productList.find((p) => p.id === productId);
    const productUnitIds = this.getProductUnitIds(product);

    return productUnitIds.length > 0
      ? this.unitOfMeasureList.filter((unit) => productUnitIds.includes(Number(unit.id)))
      : this.unitOfMeasureList;
  }

  private getProductUnitIds(product: any): number[] {
    const ids = [
      ...(Array.isArray(product?.unitOfMeasureIds) ? product.unitOfMeasureIds : []),
      ...(Array.isArray(product?.unitOfMeasures)
        ? product.unitOfMeasures.map((unit: any) => unit?.id)
        : []),
      product?.unitOfMeasureId,
      product?.unitOfMeasure?.id,
    ];

    return [...new Set(ids.map((id) => Number(id)).filter((id) => id > 0))];
  }

  private getDefaultProductUnitId(product: any): number {
    const unitIds = this.getProductUnitIds(product);
    const defaultUnitId = Number(product?.unitOfMeasureId || product?.unitOfMeasure?.id || 0);
    if (defaultUnitId && unitIds.includes(defaultUnitId)) {
      return defaultUnitId;
    }

    return unitIds.length === 1 ? unitIds[0] : 0;
  }

  removeBudgetLine(index: number): void {
    const line = this.budgetLines[index];
    if (line?.id && this.budget?.id) {
      // Eliminar desde el servidor usando BudgetLine
      this.opportunityService.deleteBudgetLine(this.budget.id, [line.id])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.budgetLines.splice(index, 1);
            this.refreshDisplayedBudgetLineRows();
            this.calculateSummary();
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.handleLineWriteError(error, 'Error deleting budget line');
          }
        });
    } else {
      // Si no tiene ID, solo eliminar localmente (línea nueva no guardada)
      this.budgetLines.splice(index, 1);
      this.refreshDisplayedBudgetLineRows();
      this.calculateSummary();
    }
  }

  isEditingCell(field: string, index: number): boolean {
    return this.editingCells[field] === index;
  }

  startEditingCell(field: string, index: number): void {
    // Solo permitir edición si está en modo edición y tiene permisos
    if (!this.isEditModeEnabled || !this.editPermissions) {
      return;
    }

    // El campo "amount" no es editable, su valor se calcula automáticamente
    if (field === 'amount') {
      return;
    }

    // Detener edición de todos los campos
    Object.keys(this.editingCells).forEach(key => {
      this.editingCells[key] = null;
    });
    this.cancelLineNoteEditing();

    // Iniciar edición del campo seleccionado
    this.editingCells[field] = index;

    // Buscar la columna en ambas configuraciones de tabla
    const column = this.tableColumns.find(col => col.key === field) ||
      this.budgetTableColumns.find(col => col.key === field);

    // Focus en el input si no es un select ni un boolean
    if (column && column.type !== 'select' && column.type !== 'boolean') {
      setTimeout(() => {
        const inputs = this.tableInputs.toArray();
        const rowInputs = inputs.filter((x) => {
          const element = x.nativeElement;
          const row = element.closest('tr');
          const rowIndex = Array.from(row?.parentElement?.children || []).indexOf(row || null);
          return rowIndex === index && element.getAttribute('data-field') === field;
        });
        if (rowInputs.length > 0) {
          rowInputs[0].nativeElement.focus();
        }
      }, 0);
    }
  }

  stopEditingCell(field: string, index: number): void {
    // Determinar qué tabla se está editando verificando en qué array existe el índice
    // Esto es necesario porque ambas tablas pueden tener el mismo campo
    let line = null;
    let actualIsQuotationLine = false;

    // Verificar primero en budgetLines
    if (index < this.budgetLines.length && this.budgetLines[index]) {
      line = this.budgetLines[index];
      actualIsQuotationLine = false;
    }
    // Si no está en budgetLines, verificar en quotationLines
    else if (index < this.quotationLines.length && this.quotationLines[index]) {
      line = this.quotationLines[index];
      actualIsQuotationLine = true;
    }

    if (!line) return;

    // NO guardar taxes aquí - se guardarán cuando se presione "Guardar"
    // Solo mantener el estado guardado para comparar después

    this.editingCells[field] = null;

    // Recalcular campos calculados en tiempo real
    if (actualIsQuotationLine) {
      this.calculateQuotationLineAmount(index);
    } else {
      this.calculateBudgetLineAmount(index);
    }

    // Recalcular resumen si es un campo numérico
    if (field === 'quantity' || field === 'priceUnit' || field === 'price' || field === 'taxes' || field === 'discount' || field === 'amount') {
      this.calculateSummary();
    }

    // Los cambios se guardan solo cuando se presiona "Guardar" (editModeAction === 'save')
    // Angular detectará los cambios automáticamente
    // this.cdr.detectChanges();
  }

  getLineNote(line: any): string {
    return (line?.customerNote || '').toString();
  }

  private restoreQuotationLineNote(index: number, fallbackNote: string): void {
    if (this.quotationLines[index] && !this.getLineNote(this.quotationLines[index]) && fallbackNote) {
      this.quotationLines[index].customerNote = fallbackNote;
    }
  }

  private restoreBudgetLineNote(index: number, fallbackNote: string): void {
    if (this.budgetLines[index] && !this.getLineNote(this.budgetLines[index]) && fallbackNote) {
      this.budgetLines[index].customerNote = fallbackNote;
    }
  }

  isEditingLineNote(index: number, context: 'quotation' | 'budget' = 'quotation'): boolean {
    return this.editingLineNoteIndex === index && this.editingLineNoteContext === context;
  }

  startEditingLineNote(line: any, index: number, event?: Event, context: 'quotation' | 'budget' = 'quotation'): void {
    event?.stopPropagation();

    if (!this.editPermissions) {
      return;
    }

    Object.keys(this.editingCells).forEach(key => {
      this.editingCells[key] = null;
    });

    this.editingLineNoteIndex = index;
    this.editingLineNoteContext = context;
    this.lineNoteDraft = this.getLineNote(line);

    setTimeout(() => {
      const input = this.lineNoteInputs?.first?.nativeElement;
      if (input) {
        input.focus();
        input.select();
      }
    }, 0);
  }

  saveLineNote(line: any, index: number, event?: Event, context: 'quotation' | 'budget' = 'quotation'): void {
    event?.stopPropagation();

    if (!this.isEditingLineNote(index, context)) {
      return;
    }

    const previousNote = this.getLineNote(line);
    const nextNote = (this.lineNoteDraft || '').trim();

    this.editingLineNoteIndex = null;
    this.editingLineNoteContext = null;
    this.lineNoteDraft = '';

    if (previousNote === nextNote) {
      return;
    }

    line.customerNote = nextNote;

    if (context === 'budget' && line.id && this.budget?.id) {
      this.persistBudgetLineNote(line, index, previousNote);
    } else if (context === 'budget' && this.budget) {
      this.budget.budgetLines = [...this.budgetLines];
      this.cdr.detectChanges();
    } else if (line.id && this.budget?.id) {
      this.persistSaleOrderLineNote(line, index, previousNote);
    } else if (this.budget) {
      this.budget.quotationLines = [...this.quotationLines];
      this.cdr.detectChanges();
    }
  }

  /**
   * CEC-687. Enseña por qué el servidor rechazó la escritura y deshace lo que se pintó de más.
   *
   * Hasta ahora estos fallos solo llegaban a `console.error`: la tabla ya había aplicado el cambio,
   * así que el usuario veía su nota nueva y el documento seguía saliendo con la vieja. El motivo lo
   * deja el interceptor en el error; `deshacer` devuelve la fila a lo último confirmado.
   */
  private handleLineWriteError(error: any, accion: string, deshacer?: () => void): void {
    console.error(accion + ':', error);

    deshacer?.();

    const mensaje = resolveLineWriteErrorMessage(error, 'Inténtalo de nuevo o avisa a soporte.');
    const { notify, notice } = shouldNotifyLineWriteError(this.lastLineWriteError, mensaje, Date.now());
    this.lastLineWriteError = notice;

    if (notify) {
      this.showLineWriteToast('No se pudo guardar el cambio en la línea', mensaje);
    }

    this.cdr.detectChanges();
  }

  private showLineWriteToast(content: string, msg: string): void {
    this.toastConfig = {
      icon: 'dangerous',
      iconColor: '#CB2A2F',
      content,
      msg,
      showUndo: false,
    };

    if (!this.notificationTmpl) {
      return;
    }

    this.notRef = this.notificationService.create(
      'blank',
      '',
      this.notificationTmpl,
      { nzPlacement: 'bottomRight', nzClass: 'custom-notification', nzDuration: 0 } // CEC-687: un fallo no se desvanece; se cierra con la X
    );
  }

  private persistSaleOrderLineNote(line: any, index: number, previousNote: string): void {
    // Reutiliza el mismo endpoint PUT de edición de líneas de pedido que usa la opción "Editar" del listado.
    // CEC-687: la nota ya se pintó en la tabla; si el guardado falla hay que devolverla a lo último guardado.
    this.updateSaleOrderLine(line, index, () => this.rollbackLineNote(line, previousNote));
  }

  private persistBudgetLineNote(line: any, index: number, previousNote: string): void {
    // Reutiliza el mismo endpoint PUT de edición de líneas de presupuesto que usa la opción "Editar" del listado.
    // CEC-687: mismo motivo que en la línea de pedido.
    this.updateBudgetLine(line, index, false, () => this.rollbackLineNote(line, previousNote));
  }

  /** CEC-687. Devuelve la nota de la fila a lo último que el servidor confirmó. */
  private rollbackLineNote(line: any, previousNote: string): void {
    if (!line) {
      return;
    }

    line.customerNote = previousNote;
  }

  cancelLineNoteEditing(event?: Event): void {
    event?.stopPropagation();
    this.editingLineNoteIndex = null;
    this.editingLineNoteContext = null;
    this.lineNoteDraft = '';
  }

  onLineNoteKeydown(event: KeyboardEvent, line: any, index: number, context: 'quotation' | 'budget' = 'quotation'): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveLineNote(line, index, event, context);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelLineNoteEditing(event);
    }
  }

  // Cancelar todas las ediciones activas
  cancelAllEditing(): void {
    const hasActiveEditing = Object.values(this.editingCells).some(index => index !== null);
    if (hasActiveEditing || this.editingLineNoteIndex !== null) {
      Object.keys(this.editingCells).forEach(key => {
        this.editingCells[key] = null;
      });
      this.cancelLineNoteEditing();
      // this.cdr.detectChanges();
    }
  }

  // Detectar clics fuera de la tabla para cancelar edición
  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: MouseEvent): void {
    if (!this.currentEditingField || this.currentEditingField.includes('cell')) {
      return;
    }

    const target = event.target as HTMLElement | null;
    const activeElement = document.activeElement as HTMLElement | null;
    const activeInputWillBlur = !!activeElement?.matches?.(
      `input[data-field="${this.currentEditingField}"], textarea[data-field="${this.currentEditingField}"]`,
    );

    if (activeInputWillBlur) {
      return;
    }

    const clickedInsideCurrentField = !!target?.closest?.(
      `[data-field="${this.currentEditingField}"], .ql-editor, .ql-toolbar`,
    );
    const clickedInsideOverlay = !!target?.closest?.(
      '.ant-select-dropdown, .ant-picker-dropdown',
    );

    if (!clickedInsideCurrentField && !clickedInsideOverlay) {
      this.stopEditingField(this.currentEditingField);
      this.cdr.detectChanges();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Evitar procesamiento concurrente
    if (this.isProcessingClick) {
      return;
    }

    // Solo ejecutar si estamos en modo edición
    if (!this.isEditModeEnabled) {
      return;
    }

    // Si hay un campo de detalle en edición (no tabla), no cancelar aquí
    // Los campos de detalle (expirationDate, partnerId, etc) se manejan por su propio blur/change
    if (this.currentEditingField && !this.currentEditingField.includes('cell')) {
      return;
    }

    // Verificar si hay alguna celda en edición
    const hasActiveEditing = Object.values(this.editingCells).some(index => index !== null);
    if (!hasActiveEditing) {
      return;
    }

    // Verificar si el clic fue dentro de una celda editable, input o picker overlay
    const target = event.target as HTMLElement;
    const clickedCell = target.closest('td.editable-cell');
    const clickedInput = target.closest('input, nz-select, nz-checkbox, nz-date-picker, nz-picker');
    const clickedPickerDropdown = target.closest('.ant-picker-dropdown, .cdk-overlay-container');

    // Si el clic fue fuera de cualquier celda editable, input o picker, cancelar edición
    if (!clickedCell && !clickedInput && !clickedPickerDropdown) {
      this.isProcessingClick = true;
      this.cancelAllEditing();
      // Resetear flag después de un pequeño delay
      setTimeout(() => {
        this.isProcessingClick = false;
      }, 100);
    }
  }

  calculateQuotationLineAmount(index: number): void {
    const line = this.quotationLines[index];
    if (!line) return;

    const quantity = line.quantity || 0;
    const priceUnit = line.priceUnit || 0;
    const discount = line.discount || 0;

    const subtotal = quantity * priceUnit;
    const discountAmount = subtotal * (discount / 100);
    // El importe en la tabla solo muestra el baseAmount (sin impuestos)
    line.amount = subtotal - discountAmount;

    // Recalcular conversión si la línea tiene conversión activa
    if (line.wasConverted) {
      this.recalculateLineConversion(line, false);
    }
  }

  calculateBudgetLineAmount(index: number): void {
    const line = this.budgetLines[index];
    if (!line) return;

    const quantity = line.quantity || 0;
    const price = line.price || 0;
    const discount = line.discount || 0;

    // El importe siempre se calcula con la cantidad estimada.
    const subtotal = quantity * price;
    const discountAmount = subtotal * (discount / 100);
    // El importe en la tabla solo muestra el baseAmount (sin impuestos)
    line.amount = subtotal - discountAmount;

    // Recalcular conversión si la línea tiene conversión activa
    if (line.wasConverted) {
      this.recalculateLineConversion(line, true);
    }
  }

  onTaxSelectOpen(index: number): void {
    // Ya no necesitamos guardar taxes anteriores, se guardan junto con los demás campos
  }

  onTaxSelectionChange(index: number, selectedValues: any): void {
    this.handleTaxSelectionChange(index, selectedValues, false);
  }

  onBudgetLineTaxSelectOpen(index: number): void {
    // Ya no necesitamos guardar taxes anteriores, se guardan junto con los demás campos
  }

  onBudgetLineTaxSelectionChange(index: number, selectedValues: any): void {
    this.handleTaxSelectionChange(index, selectedValues, true);
  }

  onQuotationLineFieldChange(field: string, index: number, value: any): void {
    const line = this.quotationLines[index];
    if (!line) return;

    // No manejar taxes aquí, se maneja en onTaxSelectionChange
    if (field === 'taxes') {
      return;
    }

    // Actualizar el valor inmediatamente
    // Si el valor es 0 (Ninguno) para sourceId o destinationId, guardar como null para consistencia
    if ((field === 'sourceId' || field === 'destinationId') && value === 0) {
      line[field] = null;
    } else {
      line[field] = value;
    }

    // Si se selecciona un producto, autocompletar campos relacionados
    if (field === 'product' && value && value !== 0) {
      // Actualizar taxes filtrados primero
      this.updateFilteredTaxesForQuotationLine(index);
      // Autocompletar campos (esto ya establece el tax si hay válidos disponibles)
      // el establecimiento del tax basándose en getValidTaxesForProduct
      this.autocompleteProductFields(index, value);
    } else if (field === 'product' && (!value || value === 0)) {
      // Si se elimina el producto, limpiar taxes
      line.taxes = [];
      this.updateFilteredTaxesForQuotationLine(index);
    }

    // Recalcular amount en tiempo real si cambian campos relacionados
    if (field === 'quantity' || field === 'priceUnit' || field === 'discount') {
      this.calculateQuotationLineAmount(index);
      this.calculateSummary();
    }

    // this.cdr.detectChanges();
  }


  onBudgetLineFieldChange(field: string, index: number, value: any): void {
    const line = this.budgetLines[index];
    if (!line) return;

    // No manejar taxes aquí, se maneja en onBudgetLineTaxSelectionChange
    if (field === 'taxes') {
      return;
    }

    // Actualizar el valor inmediatamente
    // Si el valor es 0 (Ninguno) para sourceId o destinationId, guardar como null para consistencia
    if ((field === 'sourceId' || field === 'destinationId') && value === 0) {
      line[field] = null;
    } else {
      line[field] = value;
    }

    // Si se selecciona un producto, autocompletar todos los campos relacionados
    if (field === 'product') {
      if (value && value !== 0) {
        // Actualizar taxes filtrados primero
        this.updateFilteredTaxesForBudgetLine(index);
        // Autocompletar todos los campos del producto (igual que en los drawers)
        // Esto ya establece el tax si hay válidos disponibles
        // No necesitamos validar después porque autocompleteProductFieldsForBudgetLine ya maneja correctamente
        // el establecimiento del tax basándose en getValidTaxesForProduct
        this.autocompleteProductFieldsForBudgetLine(index, value);
      } else {
        // Si se elimina el producto, limpiar taxes y actualizar taxes filtrados
        line.taxes = [];
        this.updateFilteredTaxesForBudgetLine(index);
      }
    }

    // Recalcular amount en tiempo real si cambian campos relacionados
    if (field === 'quantity' || field === 'price' || field === 'discount' || field === 'isUnitPrice') {
      this.calculateBudgetLineAmount(index);
      this.calculateSummary();
    }

    // this.cdr.detectChanges();
  }

  /**
   * Autocompleta los campos relacionados cuando se selecciona un producto
   */
  private autocompleteProductFields(lineIndex: number, productId: number): void {
    const line = this.quotationLines[lineIndex];
    if (!line) return;

    // Buscar el producto completo en la lista
    const product = this.productList.find(p => p.id === productId);
    if (!product) return;

    const productDescription = this.getProductLineDescription(product);
    if (productDescription) {
      line.name = productDescription;
    }

    const defaultUnitId = this.getDefaultProductUnitId(product);
    if (defaultUnitId) {
      line.unit = defaultUnitId;
    }

    // Autocompletar precio unitario
    // Manejar el caso donde unitPrice puede ser un objeto con parsedValue
    if (product.unitPrice !== undefined && product.unitPrice !== null) {
      line.priceUnit = typeof product.unitPrice === 'object' && product.unitPrice?.parsedValue !== undefined
        ? product.unitPrice.parsedValue
        : product.unitPrice;
    }

    // Autocompletar moneda (del company del producto) - si la línea tiene campo currency
    if (product.company?.currencyId && line.hasOwnProperty('currency')) {
      line.currency = product.company.currencyId;
    }

    // Autocompletar impuestos (single selection para quotationLines)
    // Usar la misma lógica de filtrado que getFilteredTaxesForLine para asegurar
    // que solo se asignen taxes que cumplan con las condiciones (tipo permitido y asociados al producto)
    // Para quotationLines, solo tomar el primer tax válido
    line.taxes = this.defaultTaxIdsForProduct(product);
    this.updateFilteredTaxesForLine(lineIndex, false);

    // Recalcular el amount después de autocompletar
    this.calculateQuotationLineAmount(lineIndex);
    this.calculateSummary();
    // this.cdr.detectChanges();
  }

  /**
   * Autocompleta los campos relacionados cuando se selecciona un producto en una budget line
   */
  private autocompleteProductFieldsForBudgetLine(lineIndex: number, productId: number): void {
    const line = this.budgetLines[lineIndex];
    if (!line) return;

    // Buscar el producto completo en la lista
    const product = this.productList.find(p => p.id === productId);
    if (!product) return;

    // Autocompletar nombre/descripción
    const productDescription = this.getProductLineDescription(product);
    if (productDescription) {
      line.name = productDescription;
    }

    const defaultUnitId = this.getDefaultProductUnitId(product);
    if (defaultUnitId) {
      line.unitMeasure = this.getUnitName(defaultUnitId) || '';
      line.unit = defaultUnitId;
    }

    // Autocompletar precio unitario
    // Manejar el caso donde unitPrice puede ser un objeto con parsedValue
    if (product.unitPrice !== undefined && product.unitPrice !== null) {
      line.price = typeof product.unitPrice === 'object' && product.unitPrice?.parsedValue !== undefined
        ? product.unitPrice.parsedValue
        : product.unitPrice;
    }

    // Autocompletar moneda (del company del producto)
    if (product.company?.currencyId) {
      line.currency = product.company.currencyId;
    }

    // Autocompletar impuestos (solo si no es costo ni unitario)
    const isCost = line.isCost || false;
    const isUnitPrice = line.isUnitPrice || false;
    if (!isCost && !isUnitPrice) {
      // Para budgetLines, tomar el primer tax válido (aunque pueden tener múltiples)
      line.taxes = this.defaultTaxIdsForProduct(product);
    } else {
      // Si es costo o unitario, limpiar taxes
      line.taxes = [];
    }
    this.updateFilteredTaxesForLine(lineIndex, true);

    // Recalcular el amount después de autocompletar
    this.calculateBudgetLineAmount(lineIndex);
    this.calculateSummary();
    // this.cdr.detectChanges();
  }

  saveAllBudgetLines(): void {
    if (this.currencyConversionBlocked) {
      this.showCurrencyConversionError();
      return;
    }

    // Si el presupuesto es nuevo (sin ID), sincronizar las líneas locales con el objeto budget
    // para que el componente padre las reciba antes de llamar al endpoint de creación
    if (!this.budget?.id) {
      if (this.budget) {
        this.budget.quotationLines = [...this.quotationLines];
        this.budget.budgetLines = [...this.budgetLines];
      }
      return;
    }

    if (!this.isEditModeEnabled || !this.editPermissions) {
      return;
    }

    // Filtrar y eliminar líneas vacías nuevas (sin ID y sin datos) antes de guardar
    this.quotationLines = this.quotationLines.filter((line, index) => {
      // Mantener líneas existentes (con ID) o líneas nuevas con datos
      return line.id || line.quantity > 0 || line.priceUnit > 0 || line.product || line.supplier;
    });

    // Sincronizar el objeto recibido por el padre antes de que este guarde el cabecero.
    // Evita que se use una copia vieja de quotationLines sin el supplier recien seleccionado.
    this.budget.quotationLines = [...this.quotationLines];
    this.budget.budgetLines = [...this.budgetLines];

    // Guardar todas las quotationLines que tienen ID (líneas existentes) - PUT
    this.quotationLines.forEach((line, index) => {
      if (line.id) {
        const saleOrderLine = {
          id: line.id,
          name: line.name || '',
          customerNote: line.customerNote || '',
          product: line.product,
          supplier: line.supplier,
          supplierId: this.extractEntityId(line.supplier, line.supplierId),
          quantity: line.quantity,
          unit: line.unit,
          priceUnit: line.priceUnit,
          taxes: line.taxes, // Incluir taxes directamente
          amount: line.amount,
          discount: line.discount,
          isCost: false,
          isUnitPrice: true,
          sequence: index,
          isActive: true,
          currency: line.currency || this.budget.currencyId || 1,
          associatedSale: null,
          sourceId: line.sourceId,
          destinationId: line.destinationId
        };
        this.updateSaleOrderLine(saleOrderLine, index);
      } else if (line.quantity > 0 || line.priceUnit > 0 || line.product || line.supplier) {
        // Línea nueva: crear solo si tiene datos - POST
        const saleOrderLine = {
          name: line.name || '',
          customerNote: line.customerNote || '',
          product: line.product,
          supplier: line.supplier,
          supplierId: this.extractEntityId(line.supplier, line.supplierId),
          quantity: line.quantity,
          unit: line.unit,
          priceUnit: line.priceUnit,
          taxes: Array.isArray(line.taxes) ? line.taxes : [],
          amount: line.amount,
          discount: line.discount,
          isCost: false,
          isUnitPrice: true,
          sequence: index,
          isActive: true,
          currency: line.currency || this.budget.currencyId || 1,
          associatedSale: null
        };
        this.createSaleOrderLine(saleOrderLine, index);
      }
    });

    // Guardar todas las budgetLines que tienen ID (líneas existentes) usando BudgetLine
    this.budgetLines.forEach((line, index) => {
      if (line.id) {
        this.updateBudgetLine(line, index, false);
      } else if (line.quantity > 0 || line.price > 0) {
        // Línea nueva: crear solo si tiene datos usando BudgetLine
        this.createBudgetLine(line, index, false);
      }
    });
  }

  private createBudgetLine(line: any, index: number, isQuotationLine: boolean = false): void {
    if (!this.budget?.id) {
      console.warn('No se puede crear la línea: falta ID de presupuesto');
      return;
    }

    // Extraer IDs correctamente (pueden ser objetos o números)
    const productId = typeof line.product === 'object' ? (line.product?.id || 0) : (line.product || 0);
    const supplierId = this.extractEntityId(line.supplier, line.supplierId) || 0;
    const currencyId = typeof line.currency === 'object' ? (line.currency?.id || 0) : (line.currency || this.budget.currencyId || 1);
    const unitOfMeasureId = typeof line.unit === 'object' ? (line.unit?.id || 0) : (line.unit || 0);
    const associatedSaleLineId = typeof line.associatedSale === 'object' ? (line.associatedSale?.id || 0) : (line.associatedSale || 0);

    // Helper para obtener valor de ID (null si es 0 o no existe)
    const getIdValue = (value: any): number | null => {
      return value && value !== 0 ? value : null;
    };

    // Obtener taxIds de la línea (siempre incluir, incluso si está vacío)
    const taxIds = Array.isArray(line.taxes)
      ? line.taxes.filter((t: any) => t !== null && t !== undefined && t !== 0)
      : [];

    // Construir el payload según el formato del endpoint
    const payload = {
      saleOrderId: this.budget.id,
      name: line.name || '',
      customerNote: line.customerNote || '',
      quantity: line.quantity || 0,
      discount: line.discount || 0,
      isCost: line.isCost !== undefined ? line.isCost : false,
      price: isQuotationLine ? (line.price || 0) : (line.price || 0),
      isUnitPrice: line.isUnitPrice !== undefined ? line.isUnitPrice : true,
      sequence: line.sequence !== undefined ? line.sequence : index,
      isActive: line.isActive !== undefined ? line.isActive : true,
      productId: getIdValue(productId),
      supplierId: getIdValue(supplierId),
      currencyId: getIdValue(currencyId) || this.budget.currencyId || 1,
      unitOfMeasureId: getIdValue(unitOfMeasureId),
      associatedSaleLineId: getIdValue(associatedSaleLineId),
      taxIds: taxIds
    };

    this.opportunityService.createBudgetLine(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            if (isQuotationLine) {
              // Preservar valores de conversión de la línea existente si existe
              const existingLine = this.quotationLines[index];
              const savedLineData = this.mergeLineResponseWithPayload(response.data, payload);
              const newCurrencyId = savedLineData.currencyId || this.budget?.currencyId || 1;
              const existingCurrencyId = existingLine?.currency || this.budget?.currencyId || 1;
              const currencyChanged = existingLine && existingCurrencyId !== newCurrencyId;

              const preservedConversion = existingLine && !currencyChanged ? {
                convertedAmount: existingLine.convertedAmount,
                wasConverted: existingLine.wasConverted,
                conversionTooltip: existingLine.conversionTooltip,
                conversionFromCurrencyId: existingLine.conversionFromCurrencyId,
                conversionToCurrencyId: existingLine.conversionToCurrencyId,
                conversionFromCurrencyToRefRate: existingLine.conversionFromCurrencyToRefRate,
                conversionRefToFinalRate: existingLine.conversionRefToFinalRate
              } : null;

              // Actualizar quotationLine con los datos del servidor
              this.quotationLines[index] = this.buildQuotationLineFromData(savedLineData);
              this.restoreQuotationLineNote(index, this.getLineNote(existingLine));

              // Restaurar valores de conversión si existían y la moneda no cambió
              if (preservedConversion && preservedConversion.wasConverted) {
                this.quotationLines[index].convertedAmount = preservedConversion.convertedAmount;
                this.quotationLines[index].wasConverted = preservedConversion.wasConverted;
                this.quotationLines[index].conversionTooltip = preservedConversion.conversionTooltip;
                this.quotationLines[index].conversionFromCurrencyId = preservedConversion.conversionFromCurrencyId;
                this.quotationLines[index].conversionToCurrencyId = preservedConversion.conversionToCurrencyId;
                this.quotationLines[index].conversionFromCurrencyToRefRate = preservedConversion.conversionFromCurrencyToRefRate;
                this.quotationLines[index].conversionRefToFinalRate = preservedConversion.conversionRefToFinalRate;

                // Recalcular el convertedAmount con el nuevo amount
                this.recalculateLineConversion(this.quotationLines[index], false);
              }

              // Actualizar taxes filtrados
              this.updateFilteredTaxesForLine(index, false);
            } else {
              // Preservar valores de conversión de la línea existente si existe
              const existingLine = this.budgetLines[index];
              const savedLineData = this.mergeLineResponseWithPayload(response.data, payload);
              const newCurrencyId = savedLineData.currencyId || this.budget?.currencyId || 1;
              const existingCurrencyId = existingLine?.currency || this.budget?.currencyId || 1;
              const currencyChanged = existingLine && existingCurrencyId !== newCurrencyId;

              const preservedConversion = existingLine && !currencyChanged ? {
                convertedAmount: existingLine.convertedAmount,
                wasConverted: existingLine.wasConverted,
                conversionTooltip: existingLine.conversionTooltip,
                conversionFromCurrencyId: existingLine.conversionFromCurrencyId,
                conversionToCurrencyId: existingLine.conversionToCurrencyId,
                conversionFromCurrencyToRefRate: existingLine.conversionFromCurrencyToRefRate,
                conversionRefToFinalRate: existingLine.conversionRefToFinalRate
              } : null;

              // Actualizar budgetLine con los datos del servidor
              this.budgetLines[index] = this.buildBudgetLineFromData(savedLineData);
              this.restoreBudgetLineNote(index, this.getLineNote(existingLine));
              this.expandParentSaleForAssociatedCost(this.budgetLines[index]);

              // Restaurar valores de conversión si existían y la moneda no cambió
              if (preservedConversion && preservedConversion.wasConverted) {
                this.budgetLines[index].convertedAmount = preservedConversion.convertedAmount;
                this.budgetLines[index].wasConverted = preservedConversion.wasConverted;
                this.budgetLines[index].conversionTooltip = preservedConversion.conversionTooltip;
                this.budgetLines[index].conversionFromCurrencyId = preservedConversion.conversionFromCurrencyId;
                this.budgetLines[index].conversionToCurrencyId = preservedConversion.conversionToCurrencyId;
                this.budgetLines[index].conversionFromCurrencyToRefRate = preservedConversion.conversionFromCurrencyToRefRate;
                this.budgetLines[index].conversionRefToFinalRate = preservedConversion.conversionRefToFinalRate;

                // Recalcular el convertedAmount con el nuevo amount
                this.recalculateLineConversion(this.budgetLines[index], true);
              }

              // Actualizar taxes filtrados
              this.updateFilteredTaxesForLine(index, true);
              this.refreshDisplayedBudgetLineRows();
            }
            this.calculateSummary();
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.handleLineWriteError(error, 'Error creating budget line');
        }
      });
  }

  private updateBudgetLine(line: any, index: number, isQuotationLine: boolean = false, onError?: () => void): void {
    if (!line.id || !this.budget?.id) {
      console.warn('No se puede actualizar la línea: falta ID de línea o presupuesto');
      return;
    }

    // Helper para convertir IDs opcionales: si es 0 o no existe, retorna null
    const getIdValue = (value: any): number | null => {
      return value && value !== 0 ? value : null;
    };

    // Extraer IDs correctamente (pueden ser objetos o números)
    const productId = typeof line.product === 'object' ? (line.product?.id || 0) : (line.product || 0);
    const supplierId = this.extractEntityId(line.supplier, line.supplierId) || 0;
    const currencyId = typeof line.currency === 'object' ? (line.currency?.id || 0) : (line.currency || this.budget.currencyId || 1);
    const unitOfMeasureId = typeof line.unit === 'object' ? (line.unit?.id || 0) : (line.unit || 0);
    const associatedSaleLineId = typeof line.associatedSale === 'object' ? (line.associatedSale?.id || 0) : (line.associatedSale || 0);

    // El precio puede venir como 'price' o 'priceUnit' dependiendo de la tabla
    const price = line.price !== undefined ? line.price : (line.priceUnit || 0);

    // Obtener taxIds de la línea actual (siempre incluir, incluso si está vacío)
    const taxIds = Array.isArray(line.taxes)
      ? line.taxes.filter((t: any) => t !== null && t !== undefined && t !== 0)
      : [];

    // Construir el payload según el formato del endpoint
    const payload = {
      id: line.id,
      saleOrderId: this.budget.id,
      name: line.name || '',
      customerNote: line.customerNote || '',
      quantity: line.quantity || 0,
      discount: line.discount || 0,
      isCost: line.isCost !== undefined ? line.isCost : false,
      price: price,
      isUnitPrice: line.isUnitPrice !== undefined ? line.isUnitPrice : true,
      sequence: line.sequence !== undefined ? line.sequence : index,
      isActive: line.isActive !== undefined ? line.isActive : true,
      productId: getIdValue(productId),
      supplierId: getIdValue(supplierId),
      currencyId: getIdValue(currencyId) || this.budget.currencyId || 1,
      unitOfMeasureId: getIdValue(unitOfMeasureId),
      associatedSaleLineId: getIdValue(associatedSaleLineId),
      taxIds: taxIds,
      sourceId: getIdValue(line.sourceId),
      destinationId: getIdValue(line.destinationId)
    };

    this.opportunityService.updateBudgetLine(line.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            if (isQuotationLine) {
              // Preservar valores de conversión de la línea existente
              const existingLine = this.quotationLines[index];
              const savedLineData = this.mergeLineResponseWithPayload(response.data, payload);
              const newCurrencyId = savedLineData.currencyId || this.budget?.currencyId || 1;
              const existingCurrencyId = existingLine?.currency || this.budget?.currencyId || 1;
              const currencyChanged = existingLine && existingCurrencyId !== newCurrencyId;

              const preservedConversion = existingLine && !currencyChanged ? {
                convertedAmount: existingLine.convertedAmount,
                wasConverted: existingLine.wasConverted,
                conversionTooltip: existingLine.conversionTooltip,
                conversionFromCurrencyId: existingLine.conversionFromCurrencyId,
                conversionToCurrencyId: existingLine.conversionToCurrencyId,
                conversionFromCurrencyToRefRate: existingLine.conversionFromCurrencyToRefRate,
                conversionRefToFinalRate: existingLine.conversionRefToFinalRate
              } : null;

              // Actualizar quotationLines
              this.quotationLines[index] = this.buildQuotationLineFromData(savedLineData);
              this.restoreQuotationLineNote(index, this.getLineNote(existingLine));

              // Restaurar valores de conversión si existían y la moneda no cambió
              if (preservedConversion && preservedConversion.wasConverted) {
                this.quotationLines[index].convertedAmount = preservedConversion.convertedAmount;
                this.quotationLines[index].wasConverted = preservedConversion.wasConverted;
                this.quotationLines[index].conversionTooltip = preservedConversion.conversionTooltip;
                this.quotationLines[index].conversionFromCurrencyId = preservedConversion.conversionFromCurrencyId;
                this.quotationLines[index].conversionToCurrencyId = preservedConversion.conversionToCurrencyId;
                this.quotationLines[index].conversionFromCurrencyToRefRate = preservedConversion.conversionFromCurrencyToRefRate;
                this.quotationLines[index].conversionRefToFinalRate = preservedConversion.conversionRefToFinalRate;

                // Recalcular el convertedAmount con el nuevo amount
                this.recalculateLineConversion(this.quotationLines[index], false);
              }

              // Actualizar taxes filtrados
              this.updateFilteredTaxesForLine(index, false);
            } else {
              // Preservar valores de conversión de la línea existente
              const existingLine = this.budgetLines[index];
              const savedLineData = this.mergeLineResponseWithPayload(response.data, payload);
              const newCurrencyId = savedLineData.currencyId || this.budget?.currencyId || 1;
              const existingCurrencyId = existingLine?.currency || this.budget?.currencyId || 1;
              const currencyChanged = existingLine && existingCurrencyId !== newCurrencyId;

              const preservedConversion = existingLine && !currencyChanged ? {
                convertedAmount: existingLine.convertedAmount,
                wasConverted: existingLine.wasConverted,
                conversionTooltip: existingLine.conversionTooltip,
                conversionFromCurrencyId: existingLine.conversionFromCurrencyId,
                conversionToCurrencyId: existingLine.conversionToCurrencyId,
                conversionFromCurrencyToRefRate: existingLine.conversionFromCurrencyToRefRate,
                conversionRefToFinalRate: existingLine.conversionRefToFinalRate
              } : null;

              // Actualizar budgetLines
              this.budgetLines[index] = this.buildBudgetLineFromData(savedLineData);
              this.restoreBudgetLineNote(index, this.getLineNote(existingLine));
              this.expandParentSaleForAssociatedCost(this.budgetLines[index]);

              // Restaurar valores de conversión si existían y la moneda no cambió
              if (preservedConversion && preservedConversion.wasConverted) {
                this.budgetLines[index].convertedAmount = preservedConversion.convertedAmount;
                this.budgetLines[index].wasConverted = preservedConversion.wasConverted;
                this.budgetLines[index].conversionTooltip = preservedConversion.conversionTooltip;
                this.budgetLines[index].conversionFromCurrencyId = preservedConversion.conversionFromCurrencyId;
                this.budgetLines[index].conversionToCurrencyId = preservedConversion.conversionToCurrencyId;
                this.budgetLines[index].conversionFromCurrencyToRefRate = preservedConversion.conversionFromCurrencyToRefRate;
                this.budgetLines[index].conversionRefToFinalRate = preservedConversion.conversionRefToFinalRate;

                // Recalcular el convertedAmount con el nuevo amount
                this.recalculateLineConversion(this.budgetLines[index], true);
              }

              // Actualizar taxes filtrados
              this.updateFilteredTaxesForLine(index, true);
              this.refreshDisplayedBudgetLineRows();
            }
            this.calculateSummary();
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.handleLineWriteError(error, 'Error updating budget line', onError);
        }
      });
  }

  private createSaleOrderLine(line: any, index: number): void {
    if (!this.budget?.id) {
      console.warn('No se puede crear la línea: falta ID de presupuesto');
      return;
    }

    // Helper para convertir IDs opcionales: si es 0 o no existe, retorna null
    const getIdValue = (value: any): number | null => {
      return value && value !== 0 ? value : null;
    };

    // Extraer IDs correctamente (pueden ser objetos o números)
    const productId = typeof line.product === 'object' ? (line.product?.id || 0) : (line.product || 0);
    // Usar la moneda de la línea original, no del budget
    const originalCurrencyId = line.currency;
    const currencyId = typeof originalCurrencyId === 'object' ? (originalCurrencyId?.id || 0) : (originalCurrencyId || 0);
    const unitOfMeasureId = typeof line.unit === 'object' ? (line.unit?.id || 0) : (line.unit || 0);

    // Construir el payload según el formato del endpoint SaleOrderLine
    // El precio puede venir como 'price' o 'priceUnit' dependiendo de cómo se llame
    const priceValue = line.priceUnit !== undefined ? line.priceUnit : (line.price || 0);

    // Obtener taxIds de la línea (si existen, filtrar ceros)
    const taxIds = Array.isArray(line.taxes)
      ? line.taxes.filter((t: any) => t !== null && t !== undefined && t !== 0)
      : [];

    // Construir el payload según el formato esperado por el backend
    // No incluir IDs con valor 0
    const payload: any = {
      saleOrderId: this.budget.id || 0,
      name: line.name || '',
      customerNote: line.customerNote || '',
      sequence: line.sequence !== undefined ? line.sequence : index,
      quantity: line.quantity || 0,
      priceUnit: priceValue || 0,
      discount: line.discount || 0,
      isActive: line.isActive !== undefined ? line.isActive : true
    };

    // Solo agregar productId si tiene un valor válido (no 0)
    if (productId && productId !== 0) {
      payload.productId = productId;
    }

    // Solo agregar unitOfMeasureId si tiene un valor válido (no 0)
    if (unitOfMeasureId && unitOfMeasureId !== 0) {
      payload.unitOfMeasureId = unitOfMeasureId;
    }

    // Siempre agregar currencyId: usar el de la línea o el del budget como fallback
    payload.currencyId = getIdValue(currencyId) || (line.currency || this.budget.currencyId || 1);

    // Enviar taxIds siempre explícito: el backend distingue ausente (no tocar) de [] (limpiar).
    payload.taxIds = taxIds;

    // Siempre incluir origen y destino (incluso si son null) para mantener consistencia
    const sourceIdValue = getIdValue(line.sourceId);
    const destinationIdValue = getIdValue(line.destinationId);

    payload.sourceId = sourceIdValue;
    payload.destinationId = destinationIdValue;

    this.opportunityService.createSaleOrderLine(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            // Preservar valores de conversión de la línea existente si existe
            const existingLine = this.quotationLines[index];
            const savedLineData = this.mergeLineResponseWithPayload(response.data, payload);
            const newCurrencyId = savedLineData.currencyId || this.budget?.currencyId || 1;
            const existingCurrencyId = existingLine?.currency || this.budget?.currencyId || 1;
            const currencyChanged = existingLine && existingCurrencyId !== newCurrencyId;

            const preservedConversion = existingLine && !currencyChanged ? {
              convertedAmount: existingLine.convertedAmount,
              wasConverted: existingLine.wasConverted,
              conversionTooltip: existingLine.conversionTooltip,
              conversionFromCurrencyId: existingLine.conversionFromCurrencyId,
              conversionToCurrencyId: existingLine.conversionToCurrencyId,
              conversionFromCurrencyToRefRate: existingLine.conversionFromCurrencyToRefRate,
              conversionRefToFinalRate: existingLine.conversionRefToFinalRate
            } : null;

            this.quotationLines[index] = this.buildQuotationLineFromData(savedLineData);
            this.restoreQuotationLineNote(index, this.getLineNote(existingLine));

            // Restaurar valores de conversión si existían y la moneda no cambió
            if (preservedConversion && preservedConversion.wasConverted) {
              this.quotationLines[index].convertedAmount = preservedConversion.convertedAmount;
              this.quotationLines[index].wasConverted = preservedConversion.wasConverted;
              this.quotationLines[index].conversionTooltip = preservedConversion.conversionTooltip;
              this.quotationLines[index].conversionFromCurrencyId = preservedConversion.conversionFromCurrencyId;
              this.quotationLines[index].conversionToCurrencyId = preservedConversion.conversionToCurrencyId;
              this.quotationLines[index].conversionFromCurrencyToRefRate = preservedConversion.conversionFromCurrencyToRefRate;
              this.quotationLines[index].conversionRefToFinalRate = preservedConversion.conversionRefToFinalRate;

              // Recalcular el convertedAmount con el nuevo amount
              this.recalculateLineConversion(this.quotationLines[index], false);
            }

            // Actualizar taxes filtrados
            this.updateFilteredTaxesForLine(index, false);

            this.calculateSummary();
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.handleLineWriteError(error, 'Error creating sale order line');
        }
      });
  }

  private updateSaleOrderLine(line: any, index: number, onError?: () => void): void {
    if (!line.id || !this.budget?.id) {
      console.warn('No se puede actualizar la línea: falta ID de línea o presupuesto');
      return;
    }

    // Helper para convertir IDs opcionales: si es 0 o no existe, retorna null
    const getIdValue = (value: any): number | null => {
      return value && value !== 0 ? value : null;
    };

    // Extraer IDs correctamente (pueden ser objetos o números)
    const productId = typeof line.product === 'object' ? (line.product?.id || 0) : (line.product || 0);
    const unitOfMeasureId = typeof line.unit === 'object' ? (line.unit?.id || 0) : (line.unit || 0);

    // Construir el payload según el formato del endpoint SaleOrderLine
    // El precio puede venir como 'price' o 'priceUnit' dependiendo de cómo se llame
    const priceValue = line.priceUnit !== undefined ? line.priceUnit : (line.price || 0);

    // Obtener los taxIds directamente del objeto line que se pasa. Enviar siempre un array
    // explícito (incluso vacío): el backend distingue ausente/null (no tocar) de [] (limpiar),
    // y este flujo de actualización debe poder limpiar los impuestos de la línea.
    const taxIds = Array.isArray(line.taxes) && line.taxes.length > 0
      ? line.taxes.filter((id: any) => id !== null && id !== undefined && id > 0)
      : [];

    // Obtener la moneda de la línea original
    const currentLine = this.quotationLines[index];
    const originalCurrencyId = currentLine?.currency || line.currency;
    const currencyId = typeof originalCurrencyId === 'object' ? (originalCurrencyId?.id || 0) : (originalCurrencyId || 0);

    const payload = {
      id: line.id,
      saleOrderId: this.budget.id,
      name: line.name || '',
      customerNote: line.customerNote || '',
      quantity: line.quantity || 0,
      discount: line.discount || 0,
      priceUnit: priceValue,
      sequence: line.sequence !== undefined ? line.sequence : index,
      isActive: line.isActive !== undefined ? line.isActive : true,
      productId: getIdValue(productId),
      currencyId: getIdValue(currencyId) || (currentLine?.currency || this.budget.currencyId || 1),
      unitOfMeasureId: getIdValue(unitOfMeasureId),
      taxIds: taxIds,
      sourceId: getIdValue(line.sourceId),
      destinationId: getIdValue(line.destinationId)
    };

    this.opportunityService.updateSaleOrderLine(line.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response?.data) {
            // Preservar valores de conversión de la línea existente
            const existingLine = this.quotationLines[index];
            const savedLineData = this.mergeLineResponseWithPayload(response.data, payload);
            const newCurrencyId = savedLineData.currencyId || this.budget?.currencyId || 1;
            const existingCurrencyId = existingLine?.currency || this.budget?.currencyId || 1;
            const currencyChanged = existingLine && existingCurrencyId !== newCurrencyId;

            const preservedConversion = existingLine && !currencyChanged ? {
              convertedAmount: existingLine.convertedAmount,
              wasConverted: existingLine.wasConverted,
              conversionTooltip: existingLine.conversionTooltip,
              conversionFromCurrencyId: existingLine.conversionFromCurrencyId,
              conversionToCurrencyId: existingLine.conversionToCurrencyId,
              conversionFromCurrencyToRefRate: existingLine.conversionFromCurrencyToRefRate,
              conversionRefToFinalRate: existingLine.conversionRefToFinalRate
            } : null;

            this.quotationLines[index] = this.buildQuotationLineFromData(savedLineData);
            this.restoreQuotationLineNote(index, this.getLineNote(existingLine));

            // Restaurar valores de conversión si existían y la moneda no cambió
            if (preservedConversion && preservedConversion.wasConverted) {
              this.quotationLines[index].convertedAmount = preservedConversion.convertedAmount;
              this.quotationLines[index].wasConverted = preservedConversion.wasConverted;
              this.quotationLines[index].conversionTooltip = preservedConversion.conversionTooltip;
              this.quotationLines[index].conversionFromCurrencyId = preservedConversion.conversionFromCurrencyId;
              this.quotationLines[index].conversionToCurrencyId = preservedConversion.conversionToCurrencyId;
              this.quotationLines[index].conversionFromCurrencyToRefRate = preservedConversion.conversionFromCurrencyToRefRate;
              this.quotationLines[index].conversionRefToFinalRate = preservedConversion.conversionRefToFinalRate;

              // Recalcular el convertedAmount con el nuevo amount
              this.recalculateLineConversion(this.quotationLines[index], false);
            }

            // Actualizar taxes filtrados
            this.updateFilteredTaxesForLine(index, false);

            this.calculateSummary();
            this.cdr.detectChanges();
          }
        },
        error: (error) => {
          this.handleLineWriteError(error, 'Error updating sale order line', onError);
        }
      });
  }

  getCellValue(line: any, column: BudgetTableColumn): string {
    const value = line[column.key];
    if (value === null || value === undefined || value === '') return '-';

    if (column.key === 'supplier') {
      return this.getSupplierName(value);
    }

    if (column.key === 'sourceId' || column.key === 'destinationId') {
      return this.getLocationName(value);
    }

    if (column.key === 'currency') {
      const currency = this.currencies.find(c => c.id === value);
      return currency ? currency.code : '-';
    }

    if (column.type === 'boolean') {
      return value ? 'Sí' : 'No';
    }

    if (column.format === 'number' && typeof value === 'number') {
      return value.toFixed(2);
    }

    return value.toString();
  }

  isEmptyValue(line: any, column: BudgetTableColumn): boolean {
    const value = line[column.key];
    if (column.type === 'boolean') {
      return false; // Los booleanos siempre tienen un valor
    }
    if (column.type === 'number') {
      return !value || value === 0;
    }
    return !value;
  }


  getSupplierName(supplierId: number | null): string {
    if (!supplierId) return '-';
    const supplier = this.supplierList.find(s => s.id === supplierId);
    return supplier ? supplier.name : '-';
  }

  getProductName(productId: number | null, fallbackName?: string): string {
    if (!productId) return fallbackName || '-';
    const product = this.productList.find(p => p.id === productId);
    return product ? product.name : (fallbackName || '-');
  }

  private getProductLineDescription(product: any): string {
    return product?.description || product?.name || '';
  }

  getLocationName(locationId: number | null): string {
    if (!locationId || locationId === 0) return '-';
    const location = this.locationList.find(l => l.id === locationId);
    return location ? location.name : '-';
  }

  /**
   * Extrae el valor numérico de un campo (actúa como si el valor recibido es un número)
   */
  private extractNumericValue(value: any, defaultValue: number = 0): number {
    return value || defaultValue;
  }

  /**
   * Extrae taxIds de un objeto que puede tener taxIds (array) o taxes (array de objetos SaleOrderLineTaxRel)
   */
  private extractTaxIds(data: any): number[] {
    if (!data) return [];

    // Caso 1: taxIds como array directo
    if (data.taxIds && Array.isArray(data.taxIds)) {
      return data.taxIds.filter((id: any) => id !== null && id !== undefined && id > 0);
    }

    // Caso 2: taxes como array de relaciones (SaleOrderLineTaxRel o BudgetLineTaxRel)
    if (data.taxes && Array.isArray(data.taxes)) {
      return data.taxes
        .map((rel: any) => {
          // Puede venir como rel.taxId, rel.tax?.id, o directamente como número
          if (typeof rel === 'number') {
            return rel;
          }
          return rel.taxId || (rel.tax && rel.tax.id) || null;
        })
        .filter((id: any) => id !== null && id !== undefined && id > 0);
    }

    // Caso 3: tax como objeto único (menos común)
    if (data.tax && typeof data.tax === 'object' && data.tax.id) {
      return [data.tax.id];
    }

    // Caso 4: taxId como número único (menos común)
    if (data.taxId && typeof data.taxId === 'number' && data.taxId > 0) {
      return [data.taxId];
    }

    return [];
  }

  private extractEntityId(...values: any[]): number | null {
    for (const value of values) {
      const rawId = value && typeof value === 'object' ? value.id : value;
      const numericId = Number(rawId);

      if (Number.isFinite(numericId) && numericId > 0) {
        return numericId;
      }
    }

    return null;
  }

  private mergeLineResponseWithPayload(lineData: any, payload: any): any {
    const normalizedLineData = lineData?.data || lineData || {};
    const mergedLineData = {
      ...payload,
      ...normalizedLineData
    };

    if (Object.prototype.hasOwnProperty.call(payload, 'supplierId')) {
      mergedLineData.supplierId = payload.supplierId;

      // If supplierId was cleared, avoid restoring a stale supplier object from the API response.
      if (payload.supplierId === null) {
        mergedLineData.supplier = null;
      } else if (mergedLineData.supplier) {
        const responseSupplierId = typeof mergedLineData.supplier === 'object'
          ? mergedLineData.supplier.id
          : mergedLineData.supplier;

        if (Number(responseSupplierId) !== Number(payload.supplierId)) {
          mergedLineData.supplier = null;
        }
      }
    }

    if (payload.taxIds && (!mergedLineData.taxIds || mergedLineData.taxIds.length === 0)) {
      mergedLineData.taxIds = payload.taxIds;
    }

    return mergedLineData;
  }

  /**
   * Construye un objeto de línea de quotation desde los datos del servidor
   */
  private buildQuotationLineFromData(lineData: any): any {
    const quantity = this.extractNumericValue(lineData.quantity);
    const discount = this.extractNumericValue(lineData.discount);
    const taxIds = this.extractTaxIds(lineData);
    const priceUnit = lineData.originalPriceUnit ?? lineData.priceUnit ?? lineData.price ?? 0;
    const supplierId = this.extractEntityId(lineData.supplierId, lineData.supplier);
    const productId = this.extractEntityId(lineData.productId, lineData.product);
    const unitOfMeasureId = this.extractEntityId(lineData.unitOfMeasureId, lineData.unitOfMeasure);
    const currencyId = this.extractEntityId(lineData.currencyId, lineData.currency, this.budget?.currencyId) || 1;
    const sourceId = this.extractEntityId(lineData.sourceId, lineData.source);
    const destinationId = this.extractEntityId(lineData.destinationId, lineData.destination);

    // Calcular el amount basándose en quantity, priceUnit y discount
    // en lugar de confiar en el valor del servidor (que puede ser 0 o incorrecto)
    const subtotal = quantity * priceUnit;
    const discountAmount = subtotal * (discount / 100);
    const calculatedAmount = subtotal - discountAmount;

    return {
      id: lineData.id,
      name: lineData.name || '',
      customerNote: lineData.customerNote || '',
      product: productId,
      // F-2: si el producto fue archivado, la navegación viva devuelve null (filtro global de
      // borrado lógico). La línea siempre lleva su propio nombre (lo que pinta el PDF); el
      // fallback termina ahí, nunca en '-' (decisión PO 11).
      productName: lineData.product?.name || lineData.productName || lineData.name || '',
      supplier: supplierId,
      quantity: quantity,
      unit: unitOfMeasureId,
      priceUnit: priceUnit,
      taxes: taxIds,
      taxDetails: resolveLineTaxes(lineData, this.companyTaxes),
      discount: discount,
      sourceId,
      destinationId,
      amount: lineData.originalPriceSubtotal ?? calculatedAmount,
      currency: currencyId,
      convertedAmount: lineData.convertedPriceSubtotal ?? lineData.priceSubtotal ?? calculatedAmount,
      wasConverted: false,
      conversionTooltip: ''
    };
  }

  /**
   * Construye un objeto de línea de budget desde los datos del servidor
   */
  private buildBudgetLineFromData(lineData: any): any {
    const quantity = this.extractNumericValue(lineData.quantity);
    const discount = this.extractNumericValue(lineData.discount);
    const taxIds = this.extractTaxIds(lineData);
    const price = lineData.originalPrice ?? lineData.price ?? 0;
    const isUnitPrice = lineData.isUnitPrice !== undefined ? lineData.isUnitPrice : true;
    const supplierId = this.extractEntityId(lineData.supplierId, lineData.supplier);
    const productId = this.extractEntityId(lineData.productId, lineData.product);
    const unitOfMeasureId = this.extractEntityId(lineData.unitOfMeasureId, lineData.unitOfMeasure);
    const currencyId = this.extractEntityId(lineData.currencyId, lineData.currency, this.budget?.currencyId) || 1;
    const sourceId = this.extractEntityId(lineData.sourceId, lineData.source);
    const destinationId = this.extractEntityId(lineData.destinationId, lineData.destination);

    // Calcular el amount basándose en quantity, price y discount
    // en lugar de confiar en el valor del servidor (que puede ser 0 o incorrecto)
    const subtotal = quantity * price;
    const discountAmount = subtotal * (discount / 100);
    const calculatedAmount = subtotal - discountAmount;

    const lineId = this.extractEntityId(
      lineData.id,
      lineData.budgetLineId,
      lineData.budgetLine,
      lineData.originalId
    );
    const associatedSaleLineId = this.extractEntityId(
      lineData.associatedSaleLineId,
      lineData.associatedSale,
      lineData.associatedSaleLine
    );

    return {
      id: lineId,
      name: lineData.name || '',
      customerNote: lineData.customerNote || '',
      isCost: lineData.isCost || false,
      associatedSale: associatedSaleLineId,
      unit: unitOfMeasureId,
      product: productId,
      // F-2: mismo fallback que en la línea de cotización — termina en el nombre propio de la
      // línea, nunca en '-' (decisión PO 11).
      productName: lineData.product?.name || lineData.productName || lineData.name || '',
      supplier: supplierId,
      quantity: quantity,
      unitMeasure: lineData.unitOfMeasure?.name || this.getUnitName(unitOfMeasureId) || '',
      price: price,
      taxes: taxIds,
      taxDetails: resolveLineTaxes(lineData, this.companyTaxes),
      currency: currencyId,
      discount: discount,
      sourceId,
      destinationId,
      amount: lineData.originalAmount ?? calculatedAmount,
      sequence: lineData.sequence || 0,
      isActive: lineData.isActive !== undefined ? lineData.isActive : true,
      isUnitPrice: isUnitPrice,
      convertedAmount: lineData.convertedAmount ?? lineData.amount ?? calculatedAmount,
      wasConverted: false,
      conversionTooltip: ''
    };
  }

  /**
   * Actualiza los taxes filtrados para una línea (genérico para quotation y budget)
   */
  private updateFilteredTaxesForLine(lineIndex: number, isBudgetLine: boolean): void {
    const line = isBudgetLine ? this.budgetLines[lineIndex] : this.quotationLines[lineIndex];
    const filteredTaxes = isBudgetLine ? this.filteredTaxesForBudgetLines : this.filteredTaxesForQuotationLines;

    if (!line) {
      filteredTaxes[lineIndex] = [];
      return;
    }

    // Si no hay producto, solo mostrar taxes con tipo 'None'
    if (!line.product) {
      filteredTaxes[lineIndex] = this.offerTaxes(line, this.companyTaxes.filter((tax: any) => tax.taxType === 'None'));
      return;
    }

    // Buscar el producto completo en la lista
    const product = this.productList.find(p => p.id === line.product);
    if (!product) {
      // Producto ausente de la caché (p. ej. archivado): ofrecer los taxes de la compañía del
      // tipo permitido según el contexto de la línea, no solo 'None'.
      filteredTaxes[lineIndex] = this.offerTaxes(line, getCompanyTaxesForMissingProduct(this.companyTaxes, isBudgetLine && !!line.isCost));
      return;
    }

    // Usar la función auxiliar para obtener taxes válidos
    filteredTaxes[lineIndex] = this.offerTaxes(line, this.getValidTaxesForProduct(product));
  }

  /**
   * CEC-689 · opción C: de los impuestos válidos para una línea, los que se ofrecen en su desplegable: los de la
   * compañía de la cotización —no la del conmutador, que puede ser otra— más el que la línea ya tenga, cada uno
   * una sola vez por nombre y porcentaje. Entre repetidos gana el que la línea ya tiene y, si no, el de id más bajo.
   */
  private offerTaxes(line: any, candidates: any[]): any[] {
    const own = restrictToQuoteCompany(candidates, this.quoteCompanyId, line?.taxes);
    return dedupeTaxOptions(own, this.quoteCompanyId, line?.taxes);
  }

  private get quoteCompanyId(): number | null {
    return this.budget?.companyId ?? this.opportunity?.companyId ?? this.companyService?.selectedCompanyId$?.value ?? null;
  }

  /**
   * CEC-689: el impuesto que se propone al elegir producto sigue siendo el primero válido DE LA COMPAÑÍA de
   * la cotización. El catálogo ya no viene filtrado por compañía, así que sin este filtro se propondría el
   * primero de cualquiera.
   */
  private defaultTaxIdsForProduct(product: any): number[] {
    const own = this.getValidTaxesForProduct(product).filter(
      (tax: any) => Number(tax.companyId) === Number(this.quoteCompanyId),
    );
    return own.length > 0 ? [own[0].id] : [];
  }

  /**
   * Maneja el cambio de selección de taxes (genérico para quotation y budget)
   */
  private handleTaxSelectionChange(index: number, selectedValues: any, isBudgetLine: boolean): void {
    const line = isBudgetLine ? this.budgetLines[index] : this.quotationLines[index];
    if (!line) return;

    // Actualizar el valor en la línea
    // Para quotationLines, taxes es single selection (un solo valor)
    // Para budgetLines, taxes puede ser array
    if (isBudgetLine) {
      line.taxes = Array.isArray(selectedValues)
        ? selectedValues.filter((v: any) => v !== null && v !== undefined && v !== 0)
        : [];
    } else {
      // Para quotationLines: single selection, convertir a array con un solo elemento
      if (selectedValues !== null && selectedValues !== undefined && selectedValues !== 0) {
        line.taxes = [selectedValues];
      } else {
        line.taxes = [];
      }
    }

    // Recalcular amount en tiempo real (solo visual)
    if (isBudgetLine) {
      this.calculateBudgetLineAmount(index);
    } else {
      this.calculateQuotationLineAmount(index);
    }

    this.calculateSummary();
    // this.cdr.detectChanges();
  }


  /**
   * Obtiene los taxes válidos para un producto: TODOS los impuestos de la compañía cuyo tipo
   * permite saleOk/purchaseOk del producto, igual que los dos drawers de creación de línea
   * (decisión PO 8). Ya no se restringe por `product.taxes` / `product.supplierTaxes`.
   */
  private getValidTaxesForProduct(product: any): any[] {
    return getCompanyTaxesForProduct(product, this.companyTaxes);
  }

  /**
   * Actualiza los taxes filtrados para una línea de quotation
   */
  updateFilteredTaxesForQuotationLine(lineIndex: number): void {
    this.updateFilteredTaxesForLine(lineIndex, false);
  }

  /**
   * Actualiza los taxes filtrados para una línea de budget
   */
  updateFilteredTaxesForBudgetLine(lineIndex: number): void {
    this.updateFilteredTaxesForLine(lineIndex, true);
  }

  /**
   * Valida y limpia los taxes de una quotation line si no están disponibles para el producto actual
   */
  private validateAndCleanTaxesForQuotationLine(lineIndex: number): void {
    const line = this.quotationLines[lineIndex];
    if (!line || !line.product) {
      if (line) line.taxes = [];
      return;
    }

    const validTaxIds = this.getFilteredTaxesForLine(lineIndex).map((tax: any) => tax.id);

    // Si hay taxes seleccionados, verificar que estén en la lista válida
    if (line.taxes && line.taxes.length > 0) {
      const currentTaxId = Array.isArray(line.taxes) ? line.taxes[0] : line.taxes;
      if (currentTaxId && !validTaxIds.includes(currentTaxId)) {
        // El tax seleccionado no está disponible para este producto, limpiarlo
        line.taxes = [];
      }
    }
  }

  /**
   * Valida y limpia los taxes de una budget line si no están disponibles para el producto actual
   */
  private validateAndCleanTaxesForBudgetLine(lineIndex: number): void {
    const line = this.budgetLines[lineIndex];
    if (!line || !line.product) {
      if (line) line.taxes = [];
      return;
    }

    const validTaxIds = this.getFilteredTaxesForBudgetLine(lineIndex).map((tax: any) => tax.id);

    // Si hay taxes seleccionados, verificar que estén en la lista válida
    if (line.taxes && Array.isArray(line.taxes) && line.taxes.length > 0) {
      // Filtrar solo los taxes que están en la lista válida
      line.taxes = line.taxes.filter((taxId: number) => validTaxIds.includes(taxId));
    }
  }

  getFilteredTaxesForLine(lineIndex: number): any[] {
    // Usar la propiedad cacheada si existe, sino calcularla
    if (this.filteredTaxesForQuotationLines.hasOwnProperty(lineIndex)) {
      return this.filteredTaxesForQuotationLines[lineIndex];
    }

    const line = this.quotationLines[lineIndex];
    if (!line || !line.product) {
      const taxes = this.offerTaxes(line, this.companyTaxes.filter((tax: any) => tax.taxType === 'None'));
      this.filteredTaxesForQuotationLines[lineIndex] = taxes;
      return taxes;
    }

    const product = this.productList.find(p => p.id === line.product);
    if (!product) {
      // Producto ausente de la caché: quotationLines son siempre contexto de venta.
      const taxes = this.offerTaxes(line, getCompanyTaxesForMissingProduct(this.companyTaxes, false));
      this.filteredTaxesForQuotationLines[lineIndex] = taxes;
      return taxes;
    }

    const taxes = this.offerTaxes(line, this.getValidTaxesForProduct(product));
    this.filteredTaxesForQuotationLines[lineIndex] = taxes;
    return taxes;
  }

  getFilteredTaxesForBudgetLine(lineIndex: number): any[] {
    // Usar la propiedad cacheada si existe, sino calcularla
    if (this.filteredTaxesForBudgetLines.hasOwnProperty(lineIndex)) {
      return this.filteredTaxesForBudgetLines[lineIndex];
    }

    const line = this.budgetLines[lineIndex];
    if (!line) {
      this.filteredTaxesForBudgetLines[lineIndex] = [];
      return [];
    }

    if (!line.product) {
      const taxes = this.offerTaxes(line, this.companyTaxes.filter((tax: any) => tax.taxType === 'None'));
      this.filteredTaxesForBudgetLines[lineIndex] = taxes;
      return taxes;
    }

    const product = this.productList.find(p => p.id === line.product);
    if (!product) {
      // Producto ausente de la caché: respeta isCost, igual que el resto del componente.
      const taxes = this.offerTaxes(line, getCompanyTaxesForMissingProduct(this.companyTaxes, !!line.isCost));
      this.filteredTaxesForBudgetLines[lineIndex] = taxes;
      return taxes;
    }

    const taxes = this.offerTaxes(line, this.getValidTaxesForProduct(product));
    this.filteredTaxesForBudgetLines[lineIndex] = taxes;
    return taxes;
  }

  /**
   * CEC-663: el texto de la columna «Impuestos». Recibe la LÍNEA, no sus ids, porque el impuesto se
   * resuelve con el detalle que trae la propia línea y sólo se cae al catálogo de la compañía si no
   * viene — el catálogo va filtrado por la compañía seleccionada en el conmutador, que no siempre es
   * la dueña de la cotización, y ahí es donde la columna se quedaba en «–».
   */
  getLineTaxesLabel(line: any): string {
    return formatLineTaxes(this.resolveTaxesForLine(line));
  }

  private resolveTaxesForLine(line: any): ResolvedLineTax[] {
    if (Array.isArray(line?.taxDetails) && this.taxDetailsMatchIds(line)) {
      return line.taxDetails;
    }

    // Los ids sin detalle son el rastro de una edición en la propia rejilla: el usuario eligió del
    // desplegable, que sale del catálogo, así que ahí sí es la fuente correcta.
    return resolveLineTaxes({ taxIds: line?.taxes }, this.companyTaxes);
  }

  /** El detalle cacheado deja de valer en cuanto la línea cambia de impuestos en la rejilla. */
  private taxDetailsMatchIds(line: any): boolean {
    const ids: number[] = Array.isArray(line?.taxes) ? line.taxes.map((id: any) => Number(id)) : [];
    const detailIds: number[] = line.taxDetails.map((tax: ResolvedLineTax) => Number(tax.id));

    return ids.length === detailIds.length && ids.every((id, index) => id === detailIds[index]);
  }

  private getBudgetDisplayName(): string {
    const opportunityName = (this.budget?.lead?.oportunidad || this.budget?.lead?.originalOpportunityName || this.budget?.originalOpportunityName || this.opportunity?.oportunidad || this.opportunity?.originalOpportunityName || this.budget?.lead?.name || this.opportunity?.name || '').trim();
    const budgetCode = (this.budget?.budgetCode || '').trim();

    if (opportunityName && opportunityName !== '-' && budgetCode && budgetCode !== '-') {
      return `${opportunityName} # ${budgetCode}`;
    }

    return (this.budget?.name || opportunityName || budgetCode || '-').trim();
  }

  updateComputedProperties(): void {
    if (!this.budget) return;

    this.budgetName = this.getBudgetDisplayName();
    this.saleOrderName = resolveSaleOrderName(this.budget);
    this.currentBudgetId = this.budget.id || 0;
    this.hasMultipleBudgets = (this.budgetList?.length || 0) > 1;

    // Actualizar código de moneda del presupuesto
    const currencyId = this.budget?.currencyId || 1;
    const currency = this.currencies.find(c => c.id === currencyId);
    this.budgetCurrencyCode = currency?.isoCode || 'USD';

    // Solo actualizar previousCurrencyId si no se ha inicializado o si es la primera carga
    if (this.previousCurrencyId === null) {
      this.previousCurrencyId = currencyId;
    }

    // Actualizar el stage actual basado en budget.stage (string del backend)
    const stageValue = this.budget.stage || 'Budget';
    this.currentStageValue = stageValue;
    this.currentStageName = getBudgetStageByValue(stageValue)?.name || 'Presupuesto';

    // Actualizar datos del presupuesto
    this.budgetData = {
      isActive: this.budget.isActive !== undefined ? this.budget.isActive : true,
      partnerId: this.budget.partnerId || null,
      driveUrl: this.budget.driveUrl || '',
      budgetTemplate: this.budget.budgetTemplate || null,
      expirationDate: this.budget.expirationDate || null,
      budgetDate: this.budget.budgetDate || null,
      currencyId: this.budget.currencyId || null,
      paymentConditions: this.budget.paymentConditions || null
    };

    // Actualizar datos comerciales del cliente
    this.customerCompanyName = this.getCustomerCompanyNameValue();
    this.contactName = this.getContactNameValue();

    // NO cargar líneas de presupuesto desde el presupuesto aquí
    // Las líneas solo se deben cargar cuando se carga el budget inicialmente o cuando se recibe desde el servidor
    // Si sobrescribimos aquí, perdemos las líneas cuando se cambia la moneda u otros campos
    // Las líneas se cargan en loadSaleOrderLines() cuando es necesario
    // Similar a cómo se manejan budgetLines, que NO se cargan aquí

    // Solo calcular resumen si hay líneas cargadas
    if (this.quotationLines.length > 0 || this.budgetLines.length > 0) {
      this.calculateSummary();
    }

    // Angular detectará los cambios automáticamente
    // this.cdr.detectChanges();
  }

  calculateSummary(): void {
    if (this.currencyConversionBlocked) {
      this.clearCurrencySummary();
      return;
    }

    // Calcular resumen basado en las líneas del presupuesto
    // Calcular importe base (sin impuestos) y total de impuestos por separado
    let baseAmountSum = 0;
    let totalTaxes = 0;
    const taxesMap: { [key: number]: { taxId: number, taxName: string, amount: number } } = {};

    this.quotationLines.forEach(line => {
      // Usar convertedAmount si existe y fue convertido, sino usar amount original
      const lineAmount = (line.convertedAmount !== null && line.convertedAmount !== undefined)
        ? line.convertedAmount
        : (line.amount || 0);

      baseAmountSum += lineAmount;

      // CEC-663: se resuelve con el detalle de la línea y sólo se cae al catálogo si no viene, para
      // que el Total no dependa de qué compañía tenga seleccionada el usuario en el conmutador.
      this.resolveTaxesForLine(line).forEach((tax) => {
        const taxAmount = sumLineTaxAmount(lineAmount, [tax]);

        if (!taxesMap[tax.id]) {
          taxesMap[tax.id] = { taxId: tax.id, taxName: tax.name, amount: 0 };
        }
        taxesMap[tax.id].amount += taxAmount;
        totalTaxes += taxAmount;
      });
    });

    // Convertir el mapa a array y ordenar por nombre
    this.taxesBreakdown = Object.values(taxesMap).sort((a, b) => a.taxName.localeCompare(b.taxName));

    this.baseAmount = baseAmountSum;
    // El total se calcula sumando los impuestos al importe base
    this.total = baseAmountSum + totalTaxes;
    this.discount = this.quotationLines.reduce((sum, line) => {
      const quantity = line.quantity || 0;
      const priceUnit = line.priceUnit || 0;
      const discount = line.discount || 0;
      const subtotal = quantity * priceUnit;
      return sum + (subtotal * (discount / 100));
    }, 0);

    // Calcular margen: Total de ventas (quotationLines) - Total de costos asociados
    // Los costos son las budgetLines con isCost=true que están asociadas a quotationLines (tienen associatedSale)
    const totalCostForQuotationLines = this.budgetLines.reduce((sum, line) => {
      // Solo contar costos que están asociados a una línea de venta (quotationLine)
      if (line.isCost && line.associatedSale) {
        return sum + this.getLineConvertedAmount(line);
      }
      return sum;
    }, 0);

    // Margen = Total de ventas - Total de costos asociados
    this.margin = this.total - totalCostForQuotationLines;

    // Calcular resumen de presupuesto basado en budgetLines
    // Calcular importe base (sin impuestos) y total de impuestos por separado para budgetLines
    let budgetBaseAmountSum = 0;
    let budgetTotalTaxes = 0;
    const budgetTaxesMap: { [key: number]: { taxId: number, taxName: string, amount: number } } = {};

    // No incluir costos para el cálculo de venta.
    this.budgetLines.forEach(line => {
      if (!line.isCost) {
        // Usar convertedAmount si existe y fue convertido, sino usar amount original
        const lineAmount = (line.convertedAmount !== null && line.convertedAmount !== undefined)
          ? line.convertedAmount
          : (line.amount || 0);

        budgetBaseAmountSum += lineAmount;

        // CEC-663: misma resolución que en las líneas de venta — el detalle de la línea manda sobre
        // el catálogo, que va filtrado por la compañía seleccionada y no siempre es la de la cotización.
        this.resolveTaxesForLine(line).forEach((tax) => {
          const taxAmount = sumLineTaxAmount(lineAmount, [tax]);

          if (!budgetTaxesMap[tax.id]) {
            budgetTaxesMap[tax.id] = { taxId: tax.id, taxName: tax.name, amount: 0 };
          }
          budgetTaxesMap[tax.id].amount += taxAmount;
          budgetTotalTaxes += taxAmount;
        });
      }
    });

    // Convertir el mapa a array y ordenar por nombre
    this.budgetTaxesBreakdown = Object.values(budgetTaxesMap).sort((a, b) => a.taxName.localeCompare(b.taxName));
    this.budgetBaseAmount = budgetBaseAmountSum;
    this.budgetTotalWithTaxes = budgetBaseAmountSum + budgetTotalTaxes;

    // Total venta = importe base (el amount de las líneas es baseAmount sin impuestos)
    // El usuario solicita explícitamente excluir los impuestos de este total
    this.totalSale = this.budgetBaseAmount;

    // Calcular costo total sumando los amount de las líneas que son costos
    // IMPORTANTE: El amount ya está calculado sin impuestos (baseAmount) en calculateBudgetLineAmount()
    // Además, por lógica de negocio, las líneas de costo no pueden tener impuestos asignados
    // Usar valores convertidos si existen
    this.totalCost = this.budgetLines.reduce((sum, line) => {
      if (line.isCost) {
        const lineAmount = (line.convertedAmount !== null && line.convertedAmount !== undefined)
          ? line.convertedAmount
          : (line.amount || 0);
        return sum + lineAmount;
      }
      return sum;
    }, 0);

    this.grossMargin = this.totalSale - this.totalCost;
    this.marginPercent = this.totalSale > 0 ? this.grossMargin / this.totalSale : 0;
  }

  /**
   * Verifica si ambas listas están cargadas y ejecuta la conversión de monedas
   */
  private checkAndConvertCurrencies(): void {
    if (this.quotationLinesLoaded && this.budgetLinesLoaded) {
      // Reset flags para la próxima carga
      this.quotationLinesLoaded = false;
      this.budgetLinesLoaded = false;
      this.setCurrencyConversionBlocked(true);

      // Convertir monedas después de cargar (con un pequeño delay para asegurar que todo esté listo)
      setTimeout(() => {
        this.convertAllLinesToBaseCurrency();
      }, 100);
    }
  }

  /**
   * Recalcula los totales después de cambiar la moneda del presupuesto
   */
  recalculateTotalsAfterCurrencyChange(): void {
    if (!this.budget) {
      return;
    }

    this.baseCurrencyId = this.budget.currencyId || 1;
    this.convertAllLinesToBaseCurrency();
  }

  /**
   * Convierte todas las líneas a la moneda base del presupuesto
   */
  convertAllLinesToBaseCurrency(): void {
    if (!this.budget) {
      console.warn('[Currency Conversion] No budget available');
      return;
    }

    // Intentar obtener la fecha de creación de diferentes campos posibles
    this.conversionDate = this.budget.createdOn || this.budget.chat?.createdOn || this.budget.createdAt || this.draftConversionDate;

    if (!this.conversionDate) {
      console.warn('[Currency Conversion] No creation date found, using current date');
      this.conversionDate = this.draftConversionDate;
    }

    this.baseCurrencyId = this.budget.currencyId || 1;
    const conversionGeneration = ++this.currencyConversionGeneration;
    const conversionBudget = this.budget;
    const targetCurrencyId = this.baseCurrencyId;
    this.currencyConversionError = null;
    this.setCurrencyConversionBlocked(true);

    console.log('[Currency Conversion] Starting conversion:', {
      budgetId: this.budget.id,
      baseCurrencyId: this.baseCurrencyId,
      conversionDate: this.conversionDate,
      quotationLinesCount: this.quotationLines.length,
      budgetLinesCount: this.budgetLines.length
    });

    // Si no hay líneas, solo recalcular los totales con valores vacíos
    if (this.quotationLines.length === 0 && this.budgetLines.length === 0) {
      this.completeCurrencyConversion(
        conversionGeneration,
        conversionBudget,
        targetCurrencyId,
      );
      this.calculateSummary();
      this.cdr.detectChanges();
      return;
    }

    // Convertir quotationLines
    const quotationConversions = this.quotationLines.map((line, index) => {
      const lineCurrencyId = typeof line.currency === 'object'
        ? (line.currency?.id || this.baseCurrencyId)
        : (line.currency || this.baseCurrencyId);

      console.log(`[Currency Conversion] Quotation line ${index}:`, {
        lineId: line.id,
        lineCurrencyId,
        baseCurrencyId: this.baseCurrencyId,
        amount: line.amount,
        needsConversion: lineCurrencyId !== this.baseCurrencyId
      });

      // Si ya está en la moneda base, no convertir
      if (lineCurrencyId === this.baseCurrencyId) {
        return of({
          convertedAmount: line.amount,
          wasConverted: false,
          fromCurrencyId: undefined,
          toCurrencyId: undefined,
          fromCurrencyToRefCurrencyRate: undefined,
          refCurrencyToFinalCurrencyRate: undefined,
          line
        });
      }

      console.log(`[Currency Conversion] Converting quotation line ${index} from ${lineCurrencyId} to ${this.baseCurrencyId}`);

      return this.currencyService.convertCurrency(
        line.amount,
        lineCurrencyId,
        this.baseCurrencyId,
        this.conversionDate
      ).pipe(
        map(response => {
          console.log(`[Currency Conversion] Quotation line ${index} converted:`, {
            originalAmount: line.amount,
            convertedAmount: response?.data?.convertedAmount
          });

          // Guardar las tasas de conversión para recalcular localmente después
          const fromRefRate = normalizePositiveBaseRate(
            response?.data?.fromCurrencyToRefCurrencyRate,
            'de origen',
          );
          const refToFinalRate = normalizePositiveBaseRate(
            response?.data?.refCurrencyToFinalCurrencyRate,
            'de destino',
          );

          return {
            convertedAmount: convertCurrencyAmount(line.amount, fromRefRate, refToFinalRate),
            wasConverted: true,
            fromCurrencyId: response?.data?.fromCurrencyId,
            toCurrencyId: response?.data?.toCurrencyId,
            fromCurrencyToRefCurrencyRate: fromRefRate,
            refCurrencyToFinalCurrencyRate: refToFinalRate,
            line
          };
        })
      );
    });

    // Convertir budgetLines
    const budgetConversions = this.budgetLines.map((line, index) => {
      const lineCurrencyId = typeof line.currency === 'object'
        ? (line.currency?.id || this.baseCurrencyId)
        : (line.currency || this.baseCurrencyId);

      console.log(`[Currency Conversion] Budget line ${index}:`, {
        lineId: line.id,
        lineCurrencyId,
        baseCurrencyId: this.baseCurrencyId,
        amount: line.amount,
        needsConversion: lineCurrencyId !== this.baseCurrencyId
      });

      if (lineCurrencyId === this.baseCurrencyId) {
        return of({
          convertedAmount: line.amount,
          wasConverted: false,
          fromCurrencyId: undefined,
          toCurrencyId: undefined,
          fromCurrencyToRefCurrencyRate: undefined,
          refCurrencyToFinalCurrencyRate: undefined,
          line
        });
      }

      console.log(`[Currency Conversion] Converting budget line ${index} from ${lineCurrencyId} to ${this.baseCurrencyId}`);

      return this.currencyService.convertCurrency(
        line.amount,
        lineCurrencyId,
        this.baseCurrencyId,
        this.conversionDate
      ).pipe(
        map(response => {
          console.log(`[Currency Conversion] Budget line ${index} converted:`, {
            originalAmount: line.amount,
            convertedAmount: response?.data?.convertedAmount
          });

          // Guardar las tasas de conversión para recalcular localmente después
          const fromRefRate = normalizePositiveBaseRate(
            response?.data?.fromCurrencyToRefCurrencyRate,
            'de origen',
          );
          const refToFinalRate = normalizePositiveBaseRate(
            response?.data?.refCurrencyToFinalCurrencyRate,
            'de destino',
          );

          return {
            convertedAmount: convertCurrencyAmount(line.amount, fromRefRate, refToFinalRate),
            wasConverted: true,
            fromCurrencyId: response?.data?.fromCurrencyId,
            toCurrencyId: response?.data?.toCurrencyId,
            fromCurrencyToRefCurrencyRate: fromRefRate,
            refCurrencyToFinalCurrencyRate: refToFinalRate,
            line
          };
        })
      );
    });

    // Ejecutar todas las conversiones (solo si hay conversiones que hacer)
    const allConversions = [...quotationConversions, ...budgetConversions];
    if (allConversions.length === 0) {
      this.completeCurrencyConversion(
        conversionGeneration,
        conversionBudget,
        targetCurrencyId,
      );
      // Si no hay conversiones que hacer, solo recalcular
      this.calculateSummary();
      this.cdr.detectChanges();
      return;
    }

    forkJoin(allConversions)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (results) => {
          if (!this.isCurrentCurrencyConversion(
            conversionGeneration,
            conversionBudget,
            targetCurrencyId,
          )) {
            return;
          }

          // Separar resultados de quotation y budget
          const quotationResults = results.slice(0, this.quotationLines.length);
          const budgetResults = results.slice(this.quotationLines.length);

          // Actualizar solo los flags y valores convertidos (sin cambiar amount visible)
          quotationResults.forEach((result, index) => {
            const line = this.quotationLines[index];
            line.convertedAmount = result.convertedAmount;
            line.wasConverted = result.wasConverted;
            if (result.wasConverted) {
              // Guardar las tasas de conversión para recalcular localmente después
              line.conversionFromCurrencyId = result.fromCurrencyId;
              line.conversionToCurrencyId = result.toCurrencyId;
              line.conversionFromCurrencyToRefRate = result.fromCurrencyToRefCurrencyRate;
              line.conversionRefToFinalRate = result.refCurrencyToFinalCurrencyRate;
              // Calcular el tooltip una sola vez y guardarlo en la línea
              line.conversionTooltip = this.buildCurrencyConversionTooltip(line);
            } else {
              // Limpiar tooltip y tasas si no fue convertido
              line.conversionTooltip = '';
              line.conversionFromCurrencyId = undefined;
              line.conversionToCurrencyId = undefined;
              line.conversionFromCurrencyToRefRate = undefined;
              line.conversionRefToFinalRate = undefined;
            }
          });

          budgetResults.forEach((result, index) => {
            const line = this.budgetLines[index];
            line.convertedAmount = result.convertedAmount;
            line.wasConverted = result.wasConverted;
            if (result.wasConverted) {
              // Guardar las tasas de conversión para recalcular localmente después
              line.conversionFromCurrencyId = result.fromCurrencyId;
              line.conversionToCurrencyId = result.toCurrencyId;
              line.conversionFromCurrencyToRefRate = result.fromCurrencyToRefCurrencyRate;
              line.conversionRefToFinalRate = result.refCurrencyToFinalCurrencyRate;
              // Calcular el tooltip una sola vez y guardarlo en la línea
              line.conversionTooltip = this.buildCurrencyConversionTooltip(line);
            } else {
              // Limpiar tooltip y tasas si no fue convertido
              line.conversionTooltip = '';
              line.conversionFromCurrencyId = undefined;
              line.conversionToCurrencyId = undefined;
              line.conversionFromCurrencyToRefRate = undefined;
              line.conversionRefToFinalRate = undefined;
            }
          });

          this.completeCurrencyConversion(
            conversionGeneration,
            conversionBudget,
            targetCurrencyId,
          );
          this.calculateSummary(); // Recalcular con valores convertidos
          this.cdr.detectChanges();
        },
        error: (error) => {
          if (!this.isCurrentCurrencyConversion(
            conversionGeneration,
            conversionBudget,
            targetCurrencyId,
          )) {
            return;
          }

          // Bloquear el flujo completo: nunca mezclar el importe original con la moneda destino.
          console.error('[Currency Conversion] Error converting budget lines:', error);
          this.handleCurrencyConversionFailure(
            error,
            conversionGeneration,
            conversionBudget,
            targetCurrencyId,
          );
          this.cdr.detectChanges();
        }
      });
  }

  // Navegación entre presupuestos
  private completeCurrencyConversion(
    generation: number,
    budget: any,
    targetCurrencyId: number,
  ): void {
    if (!this.isCurrentCurrencyConversion(generation, budget, targetCurrencyId)) {
      return;
    }

    this.currencyConversionError = null;
    this.setCurrencyConversionBlocked(false);
    this.showCurrencyChangeNotification = this.currencyChangePending;
    this.currencyChangePending = false;

    if (this.pendingCurrencyFieldUpdate) {
      this.fieldUpdate.emit(this.pendingCurrencyFieldUpdate);
      this.pendingCurrencyFieldUpdate = null;
    }
  }

  private handleCurrencyConversionFailure(
    error: unknown,
    generation: number,
    budget: any,
    targetCurrencyId: number,
  ): void {
    if (!this.isCurrentCurrencyConversion(generation, budget, targetCurrencyId)) {
      return;
    }

    const message = error instanceof CurrencyConversionError
      ? error.message
      : this.getCurrencyConversionErrorMessage(error);

    this.currencyConversionError = message;
    this.currencyChangePending = false;
    this.showCurrencyChangeNotification = false;
    this.setCurrencyConversionBlocked(true);
    this.invalidateCrossCurrencyAmounts();
    this.clearCurrencySummary();
    this.pendingCurrencyFieldUpdate = null;
    this.showCurrencyConversionError();
  }

  private getCurrencyConversionErrorMessage(error: unknown): string {
    const responseError = error as {
      error?: { message?: unknown; title?: unknown } | string;
      message?: unknown;
    };
    const backendError = typeof responseError?.error === 'object'
      ? responseError.error?.message || responseError.error?.title
      : responseError?.error;
    const message = backendError || responseError?.message;

    return typeof message === 'string' && message.trim()
      ? message
      : 'No se pudieron convertir todos los importes. Verificá las tasas e intentá nuevamente.';
  }

  private isCurrentCurrencyConversion(
    generation: number,
    budget: any,
    targetCurrencyId: number,
  ): boolean {
    return generation === this.currencyConversionGeneration
      && budget === this.budget
      && targetCurrencyId === (this.budget?.currencyId || 1);
  }

  private resetCurrencyConversionState(): void {
    this.currencyConversionGeneration++;
    this.currencyChangeGeneration++;
    this.currencyConversionError = null;
    this.pendingCurrencyFieldUpdate = null;
    this.currencyChangePending = false;
    this.showCurrencyChangeNotification = false;
    this.setCurrencyConversionBlocked(false);
  }

  private setCurrencyConversionBlocked(blocked: boolean): void {
    if (this.currencyConversionBlocked === blocked) {
      return;
    }

    this.currencyConversionBlocked = blocked;
    this.currencyConversionValidityChange.emit(!blocked);

    if (blocked) {
      this.clearCurrencySummary();
    }
  }

  private invalidateCrossCurrencyAmounts(): void {
    [...this.quotationLines, ...this.budgetLines].forEach(line => {
      const lineCurrencyId = typeof line?.currency === 'object' ? line.currency?.id : line?.currency;
      if (lineCurrencyId && lineCurrencyId !== this.baseCurrencyId) {
        line.convertedAmount = null;
        line.wasConverted = false;
        line.conversionTooltip = '';
        line.conversionFromCurrencyId = undefined;
        line.conversionToCurrencyId = undefined;
        line.conversionFromCurrencyToRefRate = undefined;
        line.conversionRefToFinalRate = undefined;
      }
    });
  }

  private clearCurrencySummary(): void {
    this.discount = 0;
    this.baseAmount = 0;
    this.total = 0;
    this.margin = 0;
    this.taxesBreakdown = [];
    this.totalSale = 0;
    this.totalCost = 0;
    this.grossMargin = 0;
    this.marginPercent = 0;
    this.budgetBaseAmount = 0;
    this.budgetTotalWithTaxes = 0;
    this.budgetTaxesBreakdown = [];
  }

  private showCurrencyConversionError(): void {
    this.modalService.error({
      nzTitle: 'Conversión monetaria incompleta',
      nzContent: this.currencyConversionError || 'No se puede guardar hasta completar todas las conversiones.',
      nzCentered: true,
      nzWidth: 460,
    });
  }

  changeBudget(isNext: boolean, isPrev: boolean, budgetId?: number): void {
    this.budgetChange.emit({ isNext, isPrev, budgetId });
  }

  // Acciones del header
  preview(): void {
    // Implementar vista previa
  }

  requestApproval(): void {
    // Implementar solicitud de aprobación
  }

  adoptBudget(): void {
    // Implementar adoptar presupuesto
  }

  // Métodos de edición inline
  isFieldEditing(fieldPath: string): boolean {
    return this.currentEditingField === fieldPath && this.canEditField(fieldPath);
  }

  canEditTermsDirectly(): boolean {
    return this.canInlineAutosaveField('terms');
  }

  /**
   * CEC-692. Cuándo se ofrece el botón de editar los términos: cuando se pueden editar y el editor está
   * cerrado. Abierto, no se ofrece, así el botón y el clic sobre el texto nunca abren dos veces.
   */
  canStartTermsEdit(): boolean {
    return this.canEditField('terms') && !this.isFieldEditing('terms');
  }

  canInlineAutosaveField(fieldPath: string): boolean {
    return this.editPermissions && !!this.budget?.id && this.inlineAutosaveFields.has(fieldPath);
  }

  canEditField(fieldPath: string): boolean {
    return (this.isEditModeEnabled && this.editPermissions) || this.canInlineAutosaveField(fieldPath);
  }

  isInlineAutosaveActive(fieldPath: string): boolean {
    return !this.isEditModeEnabled && this.canInlineAutosaveField(fieldPath);
  }

  startEditingField(fieldPath: string): void {
    if (!this.canEditField(fieldPath) || !this.budget) {
      return;
    }

    // Guardar el valor actual como backup
    this.backupFieldValues[fieldPath] = this.budget[fieldPath];
    this.currentEditingField = fieldPath;

    if (fieldPath === 'terms') {
      this.termsEditorContent = this.budget.terms || '';
    }

    // Para date pickers, cachear el valor inicial
    if (fieldPath === 'expirationDate' || fieldPath === 'budgetDate') {
      const dateValue = this.budget[fieldPath];
      if (dateValue) {
        try {
          this.cachedDatePickerValue = new Date(dateValue);
        } catch {
          this.cachedDatePickerValue = null;
        }
      } else {
        this.cachedDatePickerValue = null;
      }
    }

    // Para selects, abrir inmediatamente después de un pequeño delay
    if (this.isSelectField(fieldPath)) {
      setTimeout(() => {
        const selectElement = document.querySelector(`[data-field="${fieldPath}"] .ant-select-selector`);
        if (selectElement) {
          (selectElement as HTMLElement).click();
        }
      }, 50);
    }

    if (this.isDateField(fieldPath)) {
      this.datePickerEditingStartedAt = Date.now();
      setTimeout(() => this.openInlineDatePicker(fieldPath), 50);
    }
  }

  isSelectField(fieldPath: string): boolean {
    const selectFields = ['partnerId', 'currencyId', 'paymentConditions', 'budgetTemplate'];
    return selectFields.includes(fieldPath);
  }

  isDateField(fieldPath: string): boolean {
    return fieldPath === 'expirationDate' || fieldPath === 'budgetDate';
  }

  private openInlineDatePicker(fieldPath: string): void {
    if (this.currentEditingField !== fieldPath) {
      return;
    }

    const pickerInput = document.querySelector(
      `[data-field="${fieldPath}"] .ant-picker-input input`,
    ) as HTMLInputElement | null;

    pickerInput?.focus();
    pickerInput?.click();
  }

  stopEditingField(fieldPath: string): void {
    if (this.currentEditingField === fieldPath) {
      this.currentEditingField = null;
      // Limpiar el cache del date picker
      if (this.isDateField(fieldPath)) {
        this.cachedDatePickerValue = null;
      }
    }
  }

  restoreBackupValues(): void {
    if (this.budget) {
      Object.keys(this.backupFieldValues).forEach(key => {
        this.budget[key] = this.backupFieldValues[key];
      });
      this.backupFieldValues = {};
      this.currentEditingField = null;
      // Limpiar el cache del date picker
      this.cachedDatePickerValue = null;
    }
  }

  onFieldClick(fieldPath: string, event: MouseEvent): void {
    const isDirectTermsEdit = this.canInlineAutosaveField(fieldPath);

    if ((!this.isEditModeEnabled && !isDirectTermsEdit) || !this.editPermissions) return; // Sin permisos: el enlace redirigirá
    // Al editar términos: evitar que el enlace abra la URL y abrir Quill
    if ((event.target as HTMLElement)?.closest?.('a')) {
      event.preventDefault();
    }
    event.stopPropagation();
    this.startEditingField(fieldPath);
  }

  onEditOverlayOpenChange(fieldPath: string, isOpen: boolean | Event): void {
    if (isOpen === true) {
      return;
    }

    if (this.isDateField(fieldPath) && Date.now() - this.datePickerEditingStartedAt < 250) {
      return;
    }

    setTimeout(() => this.stopEditingField(fieldPath), 0);
  }

  confirmTextFieldEdit(_fieldPath: string, event: Event): void {
    event.preventDefault();
    (event.target as HTMLElement)?.blur();
  }

  onFieldBlur(fieldPath: string, event: FocusEvent | string): void {
    const isDirectTermsEdit = this.canInlineAutosaveField(fieldPath);

    if (this.currentEditingField === fieldPath || isDirectTermsEdit) {
      // Si event es un string, viene de Quill (onContentChanged)
      // Si es FocusEvent, viene de un input/textarea normal
      let newValue: string;
      if (typeof event === 'string') {
        newValue = event || '';
      } else {
        const target = event.target as HTMLInputElement;
        newValue = target?.value || '';
      }

      // Para terms, verificar que no sea un cambio duplicado o que no esté vacío cuando debería tener contenido
      if (fieldPath === 'terms') {
        const previousValue = this.backupFieldValues[fieldPath];

        // Si el último contenido emitido es igual al nuevo, no emitir (evitar duplicados)
        if (this.lastEmittedContent === newValue) {
          console.log('🚫 Ignorando emisión duplicada de terms');
          this.stopEditingField(fieldPath);
          this.updateComputedProperties();
          return;
        }

        // CRÍTICO: Si el nuevo valor está vacío pero el previousValue tenía contenido con imágenes, no emitir
        const previousHadImage = previousValue && typeof previousValue === 'string' && previousValue.includes('<img');
        const currentIsEmpty = !newValue || newValue === '' || newValue.trim() === '' || newValue.trim() === '<p><br></p>' || newValue.trim() === '<p></p>';

        if (currentIsEmpty && previousHadImage) {
          console.log('🚫 Ignorando emisión de terms vacío cuando había imagen antes');
          this.stopEditingField(fieldPath);
          this.updateComputedProperties();
          return;
        }

        // Guardar el contenido emitido
        this.lastEmittedContent = newValue;
      }

      // Actualizar el valor en el objeto budget (solo localmente, no guardar aún)
      if (this.budget) {
        this.budget[fieldPath] = newValue;
      }

      // Terms se guarda de forma independiente; el resto mantiene el flujo de Guardar.
      if (!this.isEditModeEnabled || isDirectTermsEdit) {
        this.fieldUpdate.emit({
          path: fieldPath,
          value: newValue,
          previousValue: this.backupFieldValues[fieldPath]
        });
      }

      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  onQuillContentChanged(fieldPath: string, content: string): void {
    // Si se está insertando una imagen, ignorar los cambios de ngModelChange
    // para evitar emisiones prematuras o con contenido incorrecto
    if (this.isInsertingImage) {
      return;
    }

    if ((this.currentEditingField === fieldPath || (fieldPath === 'terms' && this.canEditTermsDirectly())) && this.budget) {
      // Solo actualizar el valor local mientras se edita, NO emitir cambios
      // La normalización y emisión se hará solo en onQuillBlur
      // Guardar el contenido tal cual viene de Quill para normalizarlo después en blur
      this.budget[fieldPath] = content || '';

      if (fieldPath === 'terms') {
        this.termsEditorContent = content || '';
      }

      if (fieldPath === 'terms' && this.canEditTermsDirectly()) {
        this.scheduleTermsAutosave(content || '');
      }
    }
  }

  /**
   * CEC-692. Al pasar a OTRA cotización con el editor de términos abierto, el editor carga los de la nueva
   * y se cancela el guardado automático pendiente: si no, el texto de la anterior acabaría guardado en la
   * nueva. Si el padre vuelve a pasar la misma cotización (mismo id), no se toca lo que se está escribiendo.
   */
  resetTermsEditorForBudgetChange(previous: any, current: any): void {
    if (!previous || previous?.id === current?.id) {
      return;
    }

    if (this.termsAutosaveTimeout) {
      clearTimeout(this.termsAutosaveTimeout);
      this.termsAutosaveTimeout = undefined;
    }

    this.termsEditorContent = current?.terms || '';
  }

  private scheduleTermsAutosave(content: string): void {
    if (this.termsAutosaveTimeout) {
      clearTimeout(this.termsAutosaveTimeout);
    }

    this.termsAutosaveTimeout = setTimeout(() => {
      if (this.lastEmittedContent === content) {
        return;
      }

      this.lastEmittedContent = content;
      this.fieldUpdate.emit({
        path: 'terms',
        value: content,
        previousValue: this.backupFieldValues['terms'],
      });
    }, 500);
  }

  onQuillBlur(fieldPath: string): void {
    // Si se está insertando una imagen, ignorar el blur para evitar emisiones prematuras
    if (this.isInsertingImage) {
      return;
    }

    // Si se está agregando un link, ignorar el blur para evitar que se cierre el modo edición
    if (this.isAddingLink) {
      return;
    }

    // CEC-692: abrir un desplegable de la barra (tamaño, color, fuente, alineación…) se lleva el foco y
    // Quill avisa de «blur». No es una salida: si el foco sigue dentro del editor, no se cierra. Cubre
    // toda la barra de una vez, en vez de un parche por control como los de imagen y enlace.
    if (fieldPath === 'terms'
      && focusStaysInsideEditor(this.budgetQuillEditorHost?.nativeElement, document.activeElement)) {
      return;
    }

    // CEC-692: para los términos manda lo que hay en el editor. `budget.terms` puede traer ya la respuesta
    // de un guardado automático anterior, y guardarla aquí perdería lo último que se escribió.
    let content = (fieldPath === 'terms' ? this.termsEditorContent : this.budget?.[fieldPath]) || '';

    // Normalizar contenido vacío de Quill, pero NO si contiene imágenes u otro contenido válido
    // Verificar si el contenido tiene imágenes u otro contenido real
    const hasImages = content.includes('<img');
    const hasContent = content.replace(/<p><br><\/p>|<p><\/p>|<p><br\/><\/p>/g, '').trim().length > 0;

    // Solo normalizar a vacío si realmente está vacío (sin imágenes ni contenido)
    if (!hasImages && !hasContent) {
      const trimmed = content.trim();
      if (trimmed === '' || trimmed === '<p><br></p>' || trimmed === '<p></p>' || trimmed === '<p><br/></p>') {
        content = ''; // Usar cadena vacía, NO null
      }
    }
    // Si tiene imágenes o contenido, mantener el contenido tal cual

    // Actualizar el valor normalizado en el objeto budget
    if (this.budget) {
      this.budget[fieldPath] = content; // Asegurar que sea cadena vacía si está vacío, o el contenido completo si tiene imágenes
    }

    // Emitir el cambio solo una vez con el valor normalizado
    this.onFieldBlur(fieldPath, content);
  }

  quillImageHandler(): void {
    // IMPORTANTE: Guardar el contenido ANTES de marcar la bandera para tener referencia
    const contentBeforeInsert = this.budget?.['terms'] || '';
    console.log('🖼️ Iniciando inserción de imagen en terms, contenido antes:', contentBeforeInsert.length);

    // Marcar que se está insertando una imagen para evitar emisiones prematuras
    this.isInsertingImage = true;

    // Guardar el contenido anterior como referencia para evitar emisiones vacías
    this.lastEmittedContent = contentBeforeInsert;

    // Crear input de archivo temporal
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.style.display = 'none';
    document.body.appendChild(input);

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const imageUrl = e.target.result;
          // Insertar imagen en Quill usando el editor
          const quillEditor = document.querySelector('quill-editor');
          if (quillEditor) {
            const editorElement = quillEditor.querySelector('.ql-editor') as HTMLElement;
            if (editorElement) {
              // Buscar la instancia de Quill en el elemento
              const quillInstance = (quillEditor as any).quillEditor;
              if (quillInstance) {
                const range = quillInstance.getSelection(true);
                if (range) {
                  quillInstance.insertEmbed(range.index, 'image', imageUrl);
                  quillInstance.setSelection(range.index + 1);
                } else {
                  const length = quillInstance.getLength();
                  quillInstance.insertEmbed(length - 1, 'image', imageUrl);
                  quillInstance.setSelection(length);
                }
                // Obtener el contenido completo después de insertar la imagen
                const content = quillInstance.root.innerHTML;

                // Actualizar el valor local con el contenido que incluye la imagen
                if (this.budget) {
                  this.budget['terms'] = content;
                }

                // Esperar un momento para que Quill procese completamente la inserción
                // y luego restaurar el foco, permitir emisiones y emitir el cambio
                setTimeout(() => {
                  // Obtener el contenido final después de que Quill lo procese
                  const finalContent = quillInstance.root.innerHTML || '';

                  // Verificar que el contenido realmente tiene la imagen
                  const hasImage = finalContent.includes('<img');

                  if (hasImage) {
                    // Actualizar el valor con el contenido final que incluye la imagen
                    if (this.budget) {
                      this.budget['terms'] = finalContent;
                    }

                    // Restaurar el foco en el editor
                    quillInstance.focus();

                    // IMPORTANTE: Actualizar lastEmittedContent ANTES de permitir emisiones
                    this.lastEmittedContent = finalContent;

                    // Permitir emisiones nuevamente DESPUÉS de actualizar el valor y lastEmittedContent
                    this.isInsertingImage = false;

                    // Emitir el cambio con el contenido que incluye la imagen
                    // Usar un pequeño delay adicional para asegurar que todo esté sincronizado
                    setTimeout(() => {
                      // Verificar una vez más que el contenido tiene la imagen
                      const verifyContent = quillInstance.root.innerHTML || '';
                      if (verifyContent.includes('<img')) {
                        this.lastEmittedContent = verifyContent;
                        if (this.budget) {
                          this.budget['terms'] = verifyContent;
                        }
                        this.onFieldBlur('terms', verifyContent);
                      } else {
                        this.onFieldBlur('terms', finalContent);
                      }
                    }, 100);
                  } else {
                    // Si por alguna razón no tiene la imagen, restaurar el estado pero no emitir
                    this.isInsertingImage = false;
                    quillInstance.focus();
                  }
                }, 200);
              } else {
                this.isInsertingImage = false;
              }
            } else {
              this.isInsertingImage = false;
            }
          } else {
            this.isInsertingImage = false;
          }
        };
        reader.onerror = () => {
          this.isInsertingImage = false;
        };
        reader.readAsDataURL(file);
      } else {
        // Si no se seleccionó archivo, permitir emisiones nuevamente
        this.isInsertingImage = false;
      }
      document.body.removeChild(input);
    });

    // Si se cancela la selección de archivo, también restaurar
    input.addEventListener('cancel', () => {
      this.isInsertingImage = false;
    });

    input.click();
  }

  onSelectChange(fieldPath: string, value: any): void {
    if (this.currentEditingField === fieldPath && this.budget) {
      // Guardar valor anterior para detectar cambios de moneda
      const previousValue = this.budget[fieldPath];
      let currencyConversionRequired = false;

      // Actualizar el valor en el objeto budget (solo localmente)
      this.budget[fieldPath] = value;

      // Si es partnerId, actualizar también el objeto partner para que el avatar se muestre correctamente
      if (fieldPath === 'partnerId' && value) {
        const partner = this.partnerList.find(p => p.id === value);
        if (partner) {
          this.budget.partner = partner;
        }
      }

      // Detener la edición cuando se selecciona un valor
      this.stopEditingField(fieldPath);

      // Si es currencyId, detectar cambio y recalcular
      if (fieldPath === 'currencyId' && previousValue !== value) {
        // Si previousValue es null/undefined, usar el valor actual del budget como referencia
        const oldValue = previousValue !== null && previousValue !== undefined
          ? previousValue
          : (this.previousCurrencyId || this.budget?.currencyId);

        if (oldValue !== value) {
          currencyConversionRequired = true;
          const changedBudget = this.budget;
          const currencyChangeGeneration = ++this.currencyChangeGeneration;
          this.currencyConversionError = null;
          this.currencyChangePending = true;
          this.showCurrencyChangeNotification = false;
          this.setCurrencyConversionBlocked(true);
          this.previousCurrencyId = oldValue;
          if (!this.isEditModeEnabled) {
            this.pendingCurrencyFieldUpdate = {
              path: fieldPath,
              value,
              previousValue,
            };
          }
          // Actualizar propiedades primero para actualizar budgetCurrencyCode
          this.updateComputedProperties();
          // Recalcular totales después de actualizar propiedades
          setTimeout(() => {
            if (
              currencyChangeGeneration !== this.currencyChangeGeneration
              || changedBudget !== this.budget
              || value !== this.budget?.currencyId
            ) {
              return;
            }
            this.recalculateTotalsAfterCurrencyChange();
          }, 100);
        } else {
          // Actualizar propiedades para otros campos
          this.updateComputedProperties();
        }
      } else {
        // Actualizar propiedades para otros campos
        this.updateComputedProperties();
      }

      // Solo emitir el evento si NO estamos en modo edición
      // En modo edición, los cambios se guardarán cuando se presione "Guardar"
      if (!this.isEditModeEnabled && !currencyConversionRequired) {
        this.fieldUpdate.emit({
          path: fieldPath,
          value: value,
          previousValue: this.backupFieldValues[fieldPath]
        });
      }
    }
  }

  onDateChange(fieldPath: string, value: Date | null): void {
    if (this.currentEditingField === fieldPath && this.budget) {
      const dateValue = value ? value.toISOString() : null;

      // Actualizar el valor en el objeto budget (solo localmente)
      this.budget[fieldPath] = dateValue;

      // Solo emitir el evento si NO estamos en modo edición
      // En modo edición, los cambios se guardarán cuando se presione "Guardar"
      if (!this.isEditModeEnabled) {
        this.fieldUpdate.emit({
          path: fieldPath,
          value: dateValue,
          previousValue: this.backupFieldValues[fieldPath]
        });
      }

      this.stopEditingField(fieldPath);
      this.updateComputedProperties();
    }
  }

  // Manejo simple de cambio de fecha
  onSimpleDateChange(fieldPath: string, value: Date | null): void {
    if (!this.budget || !value) {
      return;
    }

    const dateValue = value.toISOString();

    // Actualizar el valor en el objeto budget
    this.budget[fieldPath] = dateValue;

    // Usar setTimeout para evitar conflictos con el ciclo de detección de cambios
    setTimeout(() => {
      // Solo emitir el evento si NO estamos en modo edición
      if (!this.isEditModeEnabled) {
        this.fieldUpdate.emit({
          path: fieldPath,
          value: dateValue,
          previousValue: this.backupFieldValues[fieldPath]
        });
      }

      // Detener la edición después de seleccionar
      this.stopEditingField(fieldPath);
    }, 150);
  }

  onCheckboxChange(fieldPath: string, value: boolean): void {
    if (this.budget) {
      const previousValue = this.budget[fieldPath];

      // Actualizar el valor en el objeto budget (solo localmente)
      this.budget[fieldPath] = value;

      // Solo emitir el evento si NO estamos en modo edición
      // En modo edición, los cambios se guardarán cuando se presione "Guardar"
      if (!this.isEditModeEnabled) {
        this.fieldUpdate.emit({
          path: fieldPath,
          value: value,
          previousValue
        });
      }

      this.updateComputedProperties();
    }
  }

  // Métodos helper para mostrar valores
  getPartner(partnerId: number | null): any {
    if (!partnerId) return null;
    return this.partnerList.find(p => p.id === partnerId);
  }

  getPartnerName(partnerId: number | null): string {
    if (!partnerId) return '-';
    const partner = this.getPartner(partnerId);
    if (partner) {
      return `${partner.name || ''} ${partner.surName || ''}`.trim() || '-';
    }
    return '-';
  }

  getCurrencyName(currencyId: number | null): string {
    if (!currencyId) return '-';
    const currency = this.priceLists.find(c => c.id === currencyId);
    return currency ? currency.name : '-';
  }

  getLineOriginalAmount(line: any): number {
    return line?.amount ?? 0;
  }

  getLineConvertedAmount(line: any): number {
    return line?.convertedAmount !== null && line?.convertedAmount !== undefined
      ? line.convertedAmount
      : (line?.amount || 0);
  }

  shouldShowLineEquivalent(line: any): boolean {
    const lineCurrencyId = typeof line?.currency === 'object' ? line.currency?.id : line?.currency;
    const budgetCurrencyId = this.budget?.currencyId || 1;
    return !!lineCurrencyId && lineCurrencyId !== budgetCurrencyId;
  }

  getLineEquivalentTooltip(line: any): string {
    const originalAmount = this.getLineOriginalAmount(line);
    const convertedAmount = this.getLineConvertedAmount(line);
    const originalCurrencyCode = this.getCurrencyCode(typeof line?.currency === 'object' ? line.currency?.id : line?.currency);
    const budgetCurrencyCode = this.getCurrencyCode(this.budget?.currencyId, true);
    return `${originalAmount.toFixed(2)} ${originalCurrencyCode} equivale a ${convertedAmount.toFixed(2)} ${budgetCurrencyCode}.`;
  }

  getCurrencyCode(currencyId: number | null | undefined, useBudgetFallback: boolean = false): string {
    if (!currencyId) {
      if (useBudgetFallback) {
        currencyId = this.budget?.currencyId || 1;
      } else {
        return 'USD'; // Valor por defecto cuando no hay moneda y no se debe usar el presupuesto
      }
    }
    const currency = this.currencies.find(c => c.id === currencyId);
    return currency?.isoCode || 'USD';
  }

  /**
   * Construye el texto del tooltip para líneas con conversión de moneda
   * Este método se llama una sola vez cuando se convierte la línea
   */
  /**
   * Recalcula el convertedAmount de una línea usando las tasas de conversión guardadas
   */
  private recalculateLineConversion(line: any, isBudgetLine: boolean = false): void {
    if (!line.wasConverted) {
      return;
    }

    try {
      line.convertedAmount = convertCurrencyAmount(
        line.amount,
        line.conversionFromCurrencyToRefRate,
        line.conversionRefToFinalRate,
      );
      line.conversionTooltip = this.buildCurrencyConversionTooltip(line);
    } catch (error) {
      this.handleCurrencyConversionFailure(
        error,
        this.currencyConversionGeneration,
        this.budget,
        this.budget?.currencyId || 1,
      );
    }
  }

  private buildCurrencyConversionTooltip(line: any): string {
    if (!line.wasConverted) {
      return '';
    }

    const originalCurrencyCode = this.getCurrencyCode(line.currency);
    const baseCurrencyCode = this.getCurrencyCode(this.baseCurrencyId);
    const originalAmount = line.amount || 0;
    const convertedAmount = line.convertedAmount !== null && line.convertedAmount !== undefined
      ? line.convertedAmount
      : originalAmount;

    return `El valor ${originalAmount.toFixed(2)}${originalCurrencyCode} fue convertido a ${convertedAmount.toFixed(2)}${baseCurrencyCode} para el cálculo de totales.`;
  }

  getAssociatedSaleName(id: number | null): string {
    if (!id) return '-';
    // Buscar en budgetLines (que contiene los IDs originales del backend)
    const sale = this.budgetLines.find(l => l.id === id);
    return sale ? (sale.name || '-') : '-';
  }

  getPaymentConditionName(conditionId: number | null): string {
    if (!conditionId) return '-';
    const condition = this.paymentConditions.find(c => c.id === conditionId);
    return condition ? condition.name : '-';
  }

  getBudgetTemplateName(templateId: number | null): string {
    if (!templateId) return '-';
    const template = this.budgetTemplates.find(t => t.id === templateId);
    return template ? template.name : '-';
  }

  getFormattedDate(dateString: string | null): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return '-';
    }
  }

  getDateForPicker(fieldPath: string): Date | null {
    if (!this.budget || !this.budget[fieldPath]) return null;
    try {
      return new Date(this.budget[fieldPath]);
    } catch {
      return null;
    }
  }

  // Helper method para obtener el nombre del contacto
  getContactInitials(contactPartner: any): string {
    if (!contactPartner) return '';
    const name = contactPartner.name || '';
    const surName = contactPartner.surName || '';
    const firstInitial = name.charAt(0).toUpperCase();
    const secondInitial = surName.charAt(0).toUpperCase();
    return (firstInitial + secondInitial).trim() || name.charAt(0).toUpperCase() || '';
  }

  private getPartnerDisplayName(partner: any): string {
    if (!partner) return '-';
    return `${partner.name || ''} ${partner.surName || ''}`.trim() || '-';
  }

  private getCustomerCompanyNameValue(): string {
    const customerCompany =
      this.budget?.customerCompany ||
      this.budget?.lead?.customerCompany ||
      this.budget?.partner?.parent ||
      this.budget?.lead?.partner?.parent;

    return this.getPartnerDisplayName(customerCompany);
  }

  private getContactNameValue(): string {
    const contact =
      this.budget?.contact ||
      (!this.budget?.partner?.isCompany ? this.budget?.partner : null) ||
      (!this.budget?.lead?.partner?.isCompany ? this.budget?.lead?.partner : null);

    const contactName = this.getPartnerDisplayName(contact);
    if (contactName !== '-') {
      return contactName;
    }

    if (this.budget?.partnerId) {
      const partners = this.globalValuesService.partnerlist || [];
      const partner = partners.find((p: any) => p.id === this.budget.partnerId);
      if (partner) {
        return this.getPartnerDisplayName(partner);
      }
    }

    return '-';
  }

  showLinkModal(currentUrl: string, quill: any, range: { index: number; length: number }): void {
    // Marcar que estamos agregando un link para evitar que se cierre el modo edición
    this.isAddingLink = true;
    this.linkModalUrl = currentUrl || '';
    this.linkModalHadLink = !!(currentUrl && currentUrl.trim());
    this.linkModalQuill = quill;
    this.linkModalRange = range;
    this.cdr.detectChanges();
    this.linkModalRef = this.modalService.create({
      nzTitle: currentUrl ? 'Editar enlace' : 'Agregar enlace',
      nzContent: this.linkModalTemplate,
      nzFooter: null,
      nzViewContainerRef: this.viewContainerRef,
      nzData: { component: this },
      nzBodyStyle: { padding: '24px' },
      nzCentered: true,
      nzWidth: 500,
      nzClosable: true,
      nzMaskClosable: false,
    });

    // Limpiar isAddingLink con retraso para que, si el usuario pulsó Aceptar, nuestro setTimeout(100)
    // actualice antes el contenido con el link; si cerró con Cancel/X, closeLinkModal ya la limpia.
    this.linkModalRef.afterClose.subscribe(() => {
      setTimeout(() => { this.isAddingLink = false; }, 150);
    });

    setTimeout(() => {
      if (this.linkInput?.nativeElement) {
        this.linkInput.nativeElement.focus();
        this.linkInput.nativeElement.select();
      }
    }, 200);
  }

  closeLinkModal(): void {
    this.linkModalRef?.close();
    this.linkModalQuill = null;
    this.linkModalRange = null;
    this.linkModalUrl = '';
    this.linkModalHadLink = false;
    this.isAddingLink = false;
  }

  /** Quita el formato de enlace del texto seleccionado y lo deja como texto normal. */
  removeLinkFromSelection(): void {
    if (!this.linkModalQuill || !this.linkModalRange) {
      this.closeLinkModal();
      return;
    }
    this.linkModalRef?.close();
    setTimeout(() => {
      if (!this.linkModalQuill || !this.linkModalRange) {
        this.linkModalQuill = null;
        this.linkModalRange = null;
        this.linkModalUrl = '';
        this.linkModalHadLink = false;
        this.isAddingLink = false;
        return;
      }
      this.linkModalQuill.focus();
      this.linkModalQuill.setSelection(this.linkModalRange.index, this.linkModalRange.length);
      this.linkModalQuill.format('link', false);
      if (this.budget && this.linkModalQuill.root) {
        this.budget['terms'] = this.linkModalQuill.root.innerHTML;
        this.lastEmittedContent = this.budget['terms'];
        this.cdr.detectChanges();
      }
      this.linkModalQuill = null;
      this.linkModalRange = null;
      this.linkModalUrl = '';
      this.linkModalHadLink = false;
      this.isAddingLink = false;
    }, 100);
  }

  onLinkModalEnter(): void {
    this.onLinkModalConfirm();
  }

  private normalizeLinkUrl(url: string): string {
    const u = url.trim();
    if (!u) return u;
    if (/^https?:\/\//i.test(u)) return u;
    return 'https://' + u;
  }

  onLinkModalConfirm(): void {
    const url = (this.linkModalUrl || '').trim();
    if (!url || !this.linkModalQuill || !this.linkModalRange) {
      this.closeLinkModal();
      return;
    }
    const normalized = this.normalizeLinkUrl(url);

    // Cerrar el modal primero
    this.linkModalRef?.close();

    // Usar setTimeout para asegurar que el modal se cierre antes de restaurar el foco
    setTimeout(() => {
      if (!this.linkModalQuill || !this.linkModalRange) {
        this.linkModalQuill = null;
        this.linkModalRange = null;
        this.linkModalUrl = '';
        this.isAddingLink = false;
        return;
      }

      // Restaurar el foco y la selección en el editor
      this.linkModalQuill.focus();
      this.linkModalQuill.setSelection(this.linkModalRange.index, this.linkModalRange.length);
      // Aplicar el formato de link
      this.linkModalQuill.format('link', normalized);

      // Sincronizar el HTML con link al modelo; evita que un blur previo haya guardado solo párrafos
      if (this.budget && this.linkModalQuill.root) {
        this.budget['terms'] = this.linkModalQuill.root.innerHTML;
        this.lastEmittedContent = this.budget['terms'];
        this.cdr.detectChanges();
      }

      // Limpiar las referencias y bandera después de actualizar el modelo
      this.linkModalQuill = null;
      this.linkModalRange = null;
      this.linkModalUrl = '';
      this.isAddingLink = false;
    }, 100);
  }

  sanitizeHtml(html?: string | null): any {
    if (!html) return '';
    try {
      const normalizedHtml = html
        .replace(/&nbsp;/g, ' ')
        .replace(/\u00a0/g, ' ');
      return this.sanitizer.bypassSecurityTrustHtml(normalizedHtml);
    } catch (e) {
      console.error('Error sanitizing HTML:', e);
      return '';
    }
  }

}


